import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import CardUI from '../../../widgets/layout/CardUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI.js'
import SuperUserHomePageUI from '../../../templates/SuperUserHomePageUI.js'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import SwitchUI from '../../../widgets/action/SwitchUI.js'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI.js'
import AllRepaymentNavUI from '../../../components/superUser/loanList/AllRepaymentNavUI.js'
import EMITracker2ReasonUpdateModalSF from './EMITracker2ReasonUpdateModalSF.js'
import ButtonTinyUI from '../../../widgets/action/ButtonTinyUI.js'
import EmptyListUI from '../../../widgets/output/EmptyListUI.js'
import EMITracker2AllocationUpdateModalSF from './EMITracker2AllocationUpdateModalSF.js'
import ModalUI from '../../../widgets/layout/ModalUI.js'
import TabUI from '../../../widgets/output/TabUI.js'
import '../../../components/superUser/loanList/EMITrackerSummary.css'
import { parse } from 'date-fns'

const POPUP_SCROLL_THRESHOLD = 10

const PopupTableContainer = ({ recordCount, children }) => (
    <div
        className={`emi_popup_table_container ${recordCount > POPUP_SCROLL_THRESHOLD ? 'emi_popup_table_scroll' : ''}`}
    >
        {children}
    </div>
)

const ALLOCATION_TABS = ['All', 'Good Standing', 'EWS', 'NPA']

const EMITracker2PageUI = ({ context, errorList, accountList, prepaymentAccountList, accountRList, accountNRList, totals, checked, view,
    allocationUpdateMode, onAllocationUpdateClick, onAllocationUpdateCancelClick, onAllocationUpdateSuccess, reasonUpdateMode, selectedLoanId, onReasonUpdateClick, onReasonUpdateCancelClick, onReasonUpdateSuccess, isToCollect,
    yearMonth, year, month, onPreviousClick, onNextClick, onShowDetailsChange, onViewChange, onTypeChange, onEMITrackerDownload, onRefreshClick, onSaveEMITracker, onCollectionEMI, onApprovePayables, onSaveAndApprove, saveAndApproveStep, saveSuccessMsg, onlyType, saveLoading, collectionLoading, approveLoading, auditLogsResponse, selectedAllocationTab, onAllocationTabChange, repaymentsLoading, currentAllocation, currentReason }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [showColumnDropdown, setShowColumnDropdown] = useState(false)
    const [showCollectionConfirm, setShowCollectionConfirm] = useState(false)
    const [showApprovePayablesModal, setShowApprovePayablesModal] = useState(false)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showColumnDropdown && event && event.target && !event.target.closest('.column-dropdown-container')) {
                setShowColumnDropdown(false)
            }
        }

        if (showColumnDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [showColumnDropdown])

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const initialState = {
            nach: true,
            dsra: true,
            toCollect: true,
            previousMonthToCollect: true,
            differenceToCollect: true,
            reasonToCollect: true,
            totalExpectedCollection: true,
            previousMonthCollections: true,
            diffCurrentMonthDueVsExpectedCollection: true,
            paymentInitiated: true,
            paymentInitiatedDifference: true,
            dsraCurrent: true,
            dsraAfterCollection: true,
            surplus: true,
            escc: true,
            collectionsFromSurplus: true,
            collectionsFromDSRA: true,
            collectionsFromNACH: true,
            principal: true,
            interest: true,
            emi: true,
            previousEmi: true,
            differenceEMI: true,
            intOnOverdueInt: true,
            intOnOverdueIntCurrent: true,
            intOnOverduePrincipal: true,
            intOnOverduePrincipalCurrent: true,
            overduePrincipal: true,
            overdueInterest: true,
            gst: true,
            defaultInt: true,
            overdueDefaultInt: true,
            oope: true,
            overdueOOPE: true,
            intOnOverdueOOPE: true,
            intOnOverdueOOPECurrent: true,
            tds: true,
            totalOverdue: true,
            totalCurrentMonthDue: true,
            previousMonthDues: true,
            reconciliation: true,
            allocation: true,
        }
        return initialState
    })

    // Column configuration for the dropdown
    const columnConfig = [
        { key: 'nach', label: 'Nach' },
        { key: 'dsraCurrent', label: 'DSRA Before Collection' },
        { key: 'dsraAfterCollection', label: 'DSRA After Collection' },
        { key: 'toCollect', label: 'To Collect' },
        { key: 'previousMonthToCollect', label: 'Previous To Collect' },
        { key: 'differenceToCollect', label: 'To Collect Difference' },
        { key: 'reasonToCollect', label: 'To Collect Reason' },
        { key: 'totalExpectedCollection', label: 'Total Proposed Collections' },
        { key: 'previousMonthCollections', label: 'Previous Month Collections' },
        { key: 'diffCurrentMonthDueVsExpectedCollection', label: 'Difference b/w Current Dues and Proposed Collections' },
        { key: 'paymentInitiatedDifference', label: 'Difference' },
        { key: 'dsra', label: 'DSRA Required' },
        { key: 'surplus', label: 'Surplus Balance' },
        { key: 'escc', label: 'ESCC Balance' },
        { key: 'paymentInitiated', label: 'Payment Initiated' },
        { key: 'collectionsFromSurplus', label: 'Collections from Surplus' },
        { key: 'collectionsFromDSRA', label: 'Collections from DSRA' },
        { key: 'collectionsFromNACH', label: 'Collections from NACH' },
        { key: 'principal', label: 'Principal' },
        { key: 'interest', label: 'Interest' },
        { key: 'emi', label: 'EMI' },
        { key: 'previousEmi', label: 'Previous EMI' },
        { key: 'differenceEMI', label: 'EMI Difference' },
        { key: 'intOnOverdueInt', label: 'Int On Overdue Int' },
        { key: 'intOnOverdueIntCurrent', label: 'Int on overdue Interest (Current)' },
        { key: 'intOnOverduePrincipal', label: 'Int On Overdue Principal' },
        { key: 'intOnOverduePrincipalCurrent', label: 'Int on overdue Principal (Current)' },
        { key: 'overduePrincipal', label: 'Overdue Principal' },
        { key: 'overdueInterest', label: 'Overdue Interest' },
        { key: 'gst', label: 'GST' },
        { key: 'defaultInt', label: 'Penal Charge' },
        { key: 'overdueDefaultInt', label: 'Overdue Penal Charge' },
        { key: 'oope', label: 'OOPE' },
        { key: 'overdueOOPE', label: 'Overdue OOPE' },
        { key: 'intOnOverdueOOPE', label: 'Int On Overdue OOPE' },
        { key: 'intOnOverdueOOPECurrent', label: 'Int on overdue OOPE (Current)' },
        { key: 'tds', label: 'TDS' },
        { key: 'totalOverdue', label: 'Total Overdue' },
        { key: 'totalCurrentMonthDue', label: 'Total Current Dues' },
        { key: 'previousMonthDues', label: 'Previous Month Dues' },
        { key: 'reconciliation', label: 'Reconciliation' },
        { key: 'allocation', label: 'Allocation' },
    ]

    const normalizedSearch = searchTerm.trim().toLowerCase()
    const filteredNRList = useMemo(() => {
        if (!accountNRList) return []
        if (!normalizedSearch) return accountNRList
        return accountNRList.filter(a => (a.accountName || '').toLowerCase().includes(normalizedSearch))
    }, [accountNRList, normalizedSearch])
    const filteredRList = useMemo(() => {
        if (!accountRList) return []
        if (!normalizedSearch) return accountRList
        return accountRList.filter(a => (a.accountName || '').toLowerCase().includes(normalizedSearch))
    }, [accountRList, normalizedSearch])

    // Helper to check if yearMonth is previous month
    const isPreviousMonth = (() => {
        if (!yearMonth || !yearMonth.month || !yearMonth.year) return false;
        const now = new Date();
        let prevMonth = now.getMonth(); // 0-based
        let prevYear = now.getFullYear();
        if (prevMonth === 0) {
            prevMonth = 12;
            prevYear -= 1;
        }
        // yearMonth.month is assumed 1-based
        return (
            parseInt(yearMonth.month, 10) === prevMonth &&
            parseInt(yearMonth.year, 10) === prevYear
        );
    })();

    // Helper to check if yearMonth is a past month (before current month)
    const isPastMonth = (() => {
        if (!yearMonth || !yearMonth.month || !yearMonth.year) return false;
        const now = new Date();
        const currentMonth = now.getMonth() + 1; // Convert to 1-based
        const currentYear = now.getFullYear();
        const yearMonthYear = parseInt(yearMonth.year, 10);
        const yearMonthMonth = parseInt(yearMonth.month, 10);


        return (
            yearMonthYear < currentYear ||
            (yearMonthYear === currentYear && yearMonthMonth < currentMonth)
        );
    })();

    const isRetail = onlyType === 'individual'
    const prevUrl = (yearMonth && yearMonth.prevYear && yearMonth.prevMonth)
        ? (isRetail ? `/su/repayments/2.0/${yearMonth.prevYear}/${yearMonth.prevMonth}/retail` : `/su/repayments/2.0/${yearMonth.prevYear}/${yearMonth.prevMonth}`)
        : '#'
    const nextUrl = (yearMonth && yearMonth.nextYear && yearMonth.nextMonth)
        ? (isRetail ? `/su/repayments/2.0/${yearMonth.nextYear}/${yearMonth.nextMonth}/retail` : `/su/repayments/2.0/${yearMonth.nextYear}/${yearMonth.nextMonth}`)
        : '#'

    return (
        <SuperUserHomePageUI context={context} className='pg_su_disb_lst' topnav='accounts'
            headerText='Emi Tracker' centered={repaymentsLoading && !errorList} dimension='w1600'
            page={onlyType === 'individual' ? 'emi_tracker_2.0_retail' : 'emi_tracker_2.0_business'} onTypeChange={onTypeChange}
        >
            {
                repaymentsLoading && !errorList &&
                <SpinnerComponentUI />
            }
            {
                errorList && errorList.length > 0 && (
                    <div className="alm_error_box">
                        {errorList.map((error, index) => {
                            let msg = error ? (typeof error === 'object' ? error.message || String(error) : error) : ''
                            if (typeof msg === 'string' && (msg.includes('status: 500') || msg.includes('Network error: HTTP error! status: 500'))) {
                                msg = 'Internal Server Error'
                            }
                            return msg ? <div key={index}>{msg}</div> : null
                        })}
                    </div>
                )
            }
            {
                !repaymentsLoading && accountList &&
                <>
                    <LRTableActionsUI location='top'
                        left={
                            <span className='g_flex_left g_flex_h'>
                                <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} label='Refresh' />
                                <IconButtonUI to="#" icon='icon-download' onClick={onEMITrackerDownload} />
                                <span className='g_m_left_100 g_large'><b>EMI Tracker 2.0{yearMonth ? `, ${yearMonth.monthName} ${yearMonth.year}` : ''}</b></span>
                            </span>
                        }
                        right={
                            <span className='g_flex_h g_m_right_100'>
                                <span className='g_m_left_50' />
                                <Link to={prevUrl}>Previous</Link>
                                <span className='g_m_left_100' />
                                <Link to={nextUrl}>Next</Link>
                                <span className='g_m_left_100' />
                            </span>
                        }
                    />
                    {
                        onlyType === 'business' &&
                        <AllRepaymentNavUI context={context} type='emi_tracker_2.0_business' year={totals ? totals.year : undefined} month={totals ? totals.month : undefined} />
                    }
                    {
                        onlyType === 'individual' &&
                        <AllRepaymentNavUI context={context} type='emi_tracker_2.0_retail' year={totals ? totals.year : undefined} month={totals ? totals.month : undefined} />
                    }
                    {/* TODO: Add Summary */}
                    {
                        yearMonth &&
                        <>
                            <div className='emi_allocation_tabs'>
                                {ALLOCATION_TABS.map((tab) => (
                                    <TabUI
                                        key={tab}
                                        label={tab}
                                        active={selectedAllocationTab === tab}
                                        onClick={() => onAllocationTabChange(tab)}
                                    />
                                ))}
                            </div>
                            {totals &&
                                <EMITrackerSummary2UI
                                    totals={totals}
                                    isRetail={onlyType === 'individual'}
                                    auditLogsResponse={auditLogsResponse}
                                />
                            }
                        </>
                    }
                    {saveSuccessMsg && (
                        <CardUI className='g_m_top_50 g_success'>
                            <div className='g_flex_v'>
                                <span>{saveSuccessMsg}</span>
                            </div>
                        </CardUI>
                    )}
                    {accountList && accountList.length === 0 && (
                        <CardUI className='g_m_top_50'>
                            <div className='g_muted'>No data for {yearMonth.monthName} {yearMonth.year}</div>
                        </CardUI>
                    )}
                    <br />
                    <span className='g_flex_h'>
                        <span></span>
                        <div className='g_flex_h'>
                            {console.log("Inside", auditLogsResponse)}
                            {
                              auditLogsResponse && auditLogsResponse.nachfileProcessedDateISO && !auditLogsResponse.normalModeEnabledDateISO && context && context.permissions.includes('su_superuser_business') && !isPastMonth &&
                                <div className='g_flex_h g_flex_right g_m_right_100'>
                                    <button
                                        className='g_btn'
                                        onClick={() => !collectionLoading && setShowCollectionConfirm(true)}
                                        disabled={collectionLoading}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            border: '1px solid #28a745',
                                            background: '#d4edda',
                                            color: '#155724',
                                            cursor: collectionLoading ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            marginRight: '10px',
                                            opacity: collectionLoading ? 0.8 : 1,
                                        }}
                                    >
                                        {collectionLoading && (
                                            <span style={{
                                                width: '14px', height: '14px',
                                                border: '2px solid #155724',
                                                borderTopColor: 'transparent',
                                                borderRadius: '50%',
                                                display: 'inline-block',
                                                animation: 'spin 0.7s linear infinite',
                                                flexShrink: 0,
                                            }} />
                                        )}
                                        Initiate Collection
                                    </button>
                                </div>
    
                            }
                            <div className='g_flex_h g_flex_right g_m_right_100 column-dropdown-container' style={{ position: 'relative' }}>
                                <button
                                    className='g_btn g_btn_secondary'
                                    onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        background: '#fff',
                                        color: '#333',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Select visible columns
                                    <span className='icon icon-angle-down' style={{ fontSize: '12px', marginLeft: '4px' }}></span>
                                </button>

                                {showColumnDropdown && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: '0',
                                        marginTop: '8px',
                                        background: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        zIndex: 1000,
                                        minWidth: '280px',
                                        maxHeight: '400px',
                                        overflowY: 'auto'
                                    }}>
                                        <div style={{
                                            padding: '16px',
                                            borderBottom: '1px solid #eee',
                                            background: '#f8f9fa',
                                            borderRadius: '8px 8px 0 0'
                                        }}>
                                            <h4 style={{
                                                margin: '0 0 8px 0',
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: '#333'
                                            }}>
                                                Show/Hide Columns
                                            </h4>
                                            <p style={{
                                                margin: '0',
                                                fontSize: '12px',
                                                color: '#666'
                                            }}>
                                                Select columns to display in the table
                                            </p>
                                        </div>

                                        <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee' }}>
                                            <label
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#333',
                                                    padding: '6px 0'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={columnConfig && columnConfig.every(column => columnVisibility && columnVisibility[column.key]) || false}
                                                    onChange={(e) => {
                                                        const isChecked = e && e.target && e.target.checked !== undefined ? e.target.checked : false
                                                        const newVisibility = {}
                                                        columnConfig.forEach(column => {
                                                            newVisibility[column.key] = isChecked
                                                        })
                                                        setColumnVisibility(prev => ({
                                                            ...prev,
                                                            ...newVisibility
                                                        }))
                                                    }}
                                                    style={{
                                                        marginRight: '12px',
                                                        transform: 'scale(1.1)',
                                                        accentColor: '#1976d2'
                                                    }}
                                                />
                                                <span>Show All</span>
                                            </label>
                                        </div>

                                        <div style={{ padding: '8px 0' }}>
                                            {columnConfig && columnConfig.map((column) => (
                                                <label
                                                    key={column.key}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px 16px',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s',
                                                        fontSize: '14px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        const el = e && (e.currentTarget || e.target)
                                                        if (el) el.style.backgroundColor = '#f5f5f5'
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const el = e && (e.currentTarget || e.target)
                                                        if (el) el.style.backgroundColor = 'transparent'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={columnVisibility && columnVisibility[column.key] || false}
                                                        onChange={(e) => {
                                                            try {
                                                                // Use a more defensive approach
                                                                const isChecked = (e && e.target && e.target.checked !== undefined) ? e.target.checked : false
                                                                console.log('Checkbox change for column:', column.key, 'checked:', isChecked)

                                                                setColumnVisibility(prev => {
                                                                    const newState = {
                                                                        ...prev,
                                                                        [column.key]: isChecked
                                                                    }
                                                                    console.log('New column visibility state:', newState)
                                                                    return newState
                                                                })
                                                            } catch (error) {
                                                                console.error('Error handling checkbox change for column:', column.key, error)
                                                            }
                                                        }}
                                                        style={{
                                                            marginRight: '12px',
                                                            transform: 'scale(1.1)',
                                                            accentColor: '#1976d2'
                                                        }}
                                                    />
                                                    <span style={{
                                                        color: (columnVisibility && columnVisibility[column.key]) ? '#333' : '#999',
                                                        fontWeight: (columnVisibility && columnVisibility[column.key]) ? '500' : '400'
                                                    }}>
                                                        {column.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>

                                        <div style={{
                                            padding: '12px 16px',
                                            borderTop: '1px solid #eee',
                                            background: '#f8f9fa',
                                            borderRadius: '0 0 8px 8px',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center'
                                        }}>
                                            <button
                                                onClick={() => setShowColumnDropdown(false)}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    background: '#1976d2',
                                                    cursor: 'pointer',
                                                    color: '#fff'
                                                }}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className='g_flex_h g_flex_right g_m_right_100'>
                                <SwitchUI checked={checked} onChange={onShowDetailsChange} />
                                <span className='g_muted g_small g_m_right_50 g_m_left_50'>
                                    Show Loans
                                </span>
                            </div>
                        </div>
                    </span>
                    <br />
                </>
            }
            {
                // Show this card if yearMonth is previous month and accountList is null or empty
                !repaymentsLoading && isPreviousMonth && (!accountList || accountList.length === 0) &&
                <EmptyListUI
                    context={context}
                    createEnabled={false}
                    createDisabledMessageLabel={`No saved data for ${yearMonth.monthName} ${yearMonth.year}`}
                />
            }
            {
                !repaymentsLoading && !accountList && yearMonth && yearMonth.monthName !== new Date().getMonth() && yearMonth.year !== new Date().getFullYear() &&
                <CardUI className='g_m_top_50'>
                    <div className='g_flex_h'>
                        <div>No data for {yearMonth.monthName} {yearMonth.year}</div>
                    </div>
                </CardUI>
            }
            {
                accountList && accountList.length !== 0 && (!onlyType || onlyType === 'business') &&
                <AccountDisbursementUI
                    context={context}
                    accountList={filteredNRList}
                    totals={totals}
                    type='business'
                    checked={checked}
                    view={view}
                    yearMonth={yearMonth}
                    onReasonUpdateClick={onReasonUpdateClick}
                    onAllocationUpdateClick={onAllocationUpdateClick}
                    onCollectionEMI={onCollectionEMI}
                    showCollectionConfirm={showCollectionConfirm}
                    setShowCollectionConfirm={setShowCollectionConfirm}
                    showApprovePayablesModal={showApprovePayablesModal}
                    setShowApprovePayablesModal={setShowApprovePayablesModal}
                    onApprovePayables={onApprovePayables}
                    onSaveEMITracker={onSaveEMITracker}
                    onSaveAndApprove={onSaveAndApprove}
                    saveAndApproveStep={saveAndApproveStep}
                    saveLoading={saveLoading}
                    collectionLoading={collectionLoading}
                    approveLoading={approveLoading}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    auditLogsResponse={auditLogsResponse}
                />
            }
            {
                accountList && accountList.length !== 0 && (!onlyType || onlyType === 'individual') &&
                <AccountDisbursementUI
                    context={context}
                    accountList={filteredRList}
                    totals={totals}
                    type='individual'
                    checked={checked}
                    view={view}
                    yearMonth={yearMonth}
                    onReasonUpdateClick={onReasonUpdateClick}
                    onAllocationUpdateClick={onAllocationUpdateClick}
                    onCollectionEMI={onCollectionEMI}
                    showCollectionConfirm={showCollectionConfirm}
                    setShowCollectionConfirm={setShowCollectionConfirm}
                    showApprovePayablesModal={showApprovePayablesModal}
                    setShowApprovePayablesModal={setShowApprovePayablesModal}
                    onApprovePayables={onApprovePayables}
                    onSaveEMITracker={onSaveEMITracker}
                    onSaveAndApprove={onSaveAndApprove}
                    saveAndApproveStep={saveAndApproveStep}
                    saveLoading={saveLoading}
                    collectionLoading={collectionLoading}
                    approveLoading={approveLoading}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    auditLogsResponse={auditLogsResponse}
                />
            }
            <EMITracker2ReasonUpdateModalSF
                context={context}
                selectedLoanId={selectedLoanId}
                currentReason={currentReason}
                updateMode={reasonUpdateMode}
                onCancelClick={onReasonUpdateCancelClick}
                onRefreshClick={onRefreshClick}
                onReasonUpdateSuccess={onReasonUpdateSuccess}
                year={year || (yearMonth && yearMonth.year)}
                month={month || (yearMonth && yearMonth.month)}
                isToCollect={isToCollect}
            />
            <EMITracker2AllocationUpdateModalSF
                context={context}
                selectedLoanId={selectedLoanId}
                currentAllocation={currentAllocation}
                updateMode={allocationUpdateMode}
                onCancelClick={onAllocationUpdateCancelClick}
                onAllocationUpdateSuccess={onAllocationUpdateSuccess}
            />
        </SuperUserHomePageUI>
    )
}


const TOTAL_FIELD_KEYS = [
    'nach',
    'dsra',
    'toCollect',
    'previousMonthToCollect',
    'differenceToCollect',
    'totalExpectedCollection',
    'previousMonthCollections',
    'diffCurrentMonthDueVsExpectedCollection',
    'paymentInitiated',
    'paymentInitiatedDifference',
    'dsraCurrent',
    'dsraAfterCollection',
    'surplus',
    'escc',
    'collectionsFromSurplus',
    'collectionsFromDSRA',
    'collectionsFromNACH',
    'principal',
    'interest',
    'emi',
    'previousEmi',
    'differenceEMI',
    'intOnOverdueInt',
    'intOnOverdueIntCurrent',
    'intOnOverduePrincipal',
    'intOnOverduePrincipalCurrent',
    'overduePrincipal',
    'overdueInterest',
    'gst',
    'defaultInt',
    'overdueDefaultInt',
    'oope',
    'overdueOOPE',
    'intOnOverdueOOPE',
    'intOnOverdueOOPECurrent',
    'tds',
    'totalOverdue',
    'totalCurrentMonthDue',
    'previousMonthDues',
    'reconciliation'
]

const TOTAL_FIELD_KEYS_WITHOUT_NACH = TOTAL_FIELD_KEYS.filter(key => key !== 'nach')

const NUMERIC_COLUMNS = new Set([
    'nach',
    'dsra',
    'toCollect',
    'previousMonthToCollect',
    'differenceToCollect',
    'totalExpectedCollection',
    'previousMonthCollections',
    'diffCurrentMonthDueVsExpectedCollection',
    'paymentInitiated',
    'paymentInitiatedDifference',
    'dsraCurrent',
    'dsraAfterCollection',
    'surplus',
    'escc',
    'collectionsFromSurplus',
    'collectionsFromDSRA',
    'collectionsFromNACH',
    'principal',
    'interest',
    'emi',
    'previousEmi',
    'differenceEMI',
    'intOnOverdueInt',
    'intOnOverdueIntCurrent',
    'intOnOverduePrincipal',
    'intOnOverduePrincipalCurrent',
    'overduePrincipal',
    'overdueInterest',
    'gst',
    'defaultInt',
    'overdueDefaultInt',
    'oope',
    'overdueOOPE',
    'intOnOverdueOOPE',
    'intOnOverdueOOPECurrent',
    'tds',
    'totalOverdue',
    'totalCurrentMonthDue',
    'previousMonthDues',
    'reconciliation'
])

const ACCOUNT_ONLY_FILTER_KEYS = new Set([
    'nach',
    'dsraCurrent',
    'dsraAfterCollection',
    'escc',
    'surplus',
    'collectionsFromSurplus',
    'collectionsFromDSRA',
    'collectionsFromNACH',
    'previousMonthDues',
    'previousMonthCollections',
    'diffCurrentMonthDueVsExpectedCollection',
    'allocation',
    'paymentInitiated',
    'paymentInitiatedDifference'
])

// Mapping from display values to actual stored values in reconciliationAllocation
const ALLOCATION_VALUE_MAP = {
    'Expected Default': 'Default Expected',
    'NPA': 'Default Expected',
    'Expected NACH': 'Nach Expected',
    'Expected Inflow': 'Inflow Expected',
    "EWS": "EWS"
}

const getFirstAvailableValue = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== '') {
            return value
        }
    }
    return '0'
}

const getLoanFilterValue = (loan, key) => {
    if (!loan) return undefined
    switch (key) { /// Missing
        case 'dsra': return loan.dsraF4
        case 'toCollect': return loan.toCollectF4
        case 'previousMonthToCollect': return loan.toCollectPreviousMonthF4
        case 'differenceToCollect': return loan.toCollectDifferenceF4
        case 'reasonToCollect': return loan.reasonForCollectDifference
        case 'totalExpectedCollection': return loan.expectedCollectionAmountF4
        case 'dsraCurrent': return loan.dsraCurrentF4 // Added if exists in your object
        case 'principal': return loan.principalF4
        case 'interest': return loan.interestF4
        case 'emi': return loan.emiF4
        case 'previousEmi': return loan.previousMonthEMIF4
        case 'differenceEMI': return loan.emiDifferenceF4
        case 'intOnOverdueInt': return loan.intOnOverdueIntF4
        case 'intOnOverdueIntCurrent': return loan.intOnOverdueIntCurrentF4
        case 'intOnOverduePrincipal': return loan.intOnOverduePrincipalF4
        case 'intOnOverduePrincipalCurrent': return loan.intOnOverduePrincipalCurrentF4
        case 'overduePrincipal': return loan.overduePrincipalF4
        case 'overdueInterest': return loan.overdueInterestF4
        case 'gst': return loan.gstF4
        case 'defaultInt': return loan.defaultIntF4
        case 'overdueDefaultInt': return loan.overdueDefaultIntF4
        case 'oope': return loan.oopeF4
        case 'overdueOOPE': return loan.overdueOOPEF4
        case 'intOnOverdueOOPE': return loan.intOnOverdueOOPEF4
        case 'intOnOverdueOOPECurrent': return loan.intOnOverdueOOPECurrentF4
        case 'tds': return loan.tdsF4
        case 'totalOverdue': return loan.overdueAmountF4
        case 'totalCurrentMonthDue': return loan.currentMonthDueAmountF4
        case 'reconciliation': return loan.reconciliationF4
        default: return undefined
    }
}

const ACCOUNT_TOTAL_FIELD_RESOLVERS = {
    // For totals, keep NACH as numeric (EMI where NACH is active)
    nach: (account) => account && account.nachTransfer ? account.emiTotalF4 : '0',
    dsra: (account) => account ? account.dsraTotalF4 : '0',
    toCollect: (account) => account ? account.toCollectTotalF4 : '0',
    previousMonthToCollect: (account) => account ? account.toCollectPreviousMonthTotalF4 : '0',
    differenceToCollect: (account) => account ? account.toCollectDifferenceTotalF4 : '0',
    // Missing: reasonForDifference
    totalExpectedCollection: (account) => account ? account.totalExpectedCollectionAmountF4 : '0',
    previousMonthCollections: (account) => account ? account.previousMonthPaymentInitiated : '0',
    diffCurrentMonthDueVsExpectedCollection: (account) => account ? account.diffCurrentMonthDueVsExpectedCollection : '0',
    paymentInitiated: (account) => account ? account.totalPaymentInitiatedAmountF4 : '0',
    paymentInitiatedDifference: (account) => {
        if (!account) return '0'
        return (
            parseFloat(account.totalExpectedCollectionAmountF4 || 0) -
            parseFloat(account.totalPaymentInitiatedAmountF4 || 0)
        ).toFixed(2)
    },
    dsraCurrent: (account) => account ? account.dsraCurrentTotalF4 : '0',
    dsraAfterCollection: (account) => account ? account.dsraAfterCollectionTotalF4 : '0',
    surplus: (account) => account ? getFirstAvailableValue(account.surplusBalanceF4, account.surplusBalanceTotalF4) : '0',
    escc: (account) => account ? getFirstAvailableValue(account.esccBalanceF4, account.esccBalanceTotalF4) : '0',
    collectionsFromSurplus: (account) => account ? getFirstAvailableValue(account.totalCollectionsFromSurplusBalanceTotalF4, account.totalCollectionsFromSurplusBalance) : '0',
    collectionsFromDSRA: (account) => account ? getFirstAvailableValue(account.totalCollectionsFromDSRABalanceTotalF4, account.totalCollectionsFromDSRABalance) : '0',
    collectionsFromNACH: (account) => account ? getFirstAvailableValue(account.totalCollectionsFromNACHBalanceTotalF4, account.totalCollectionsFromNACHBalance) : '0',
    principal: (account) => account ? account.principalTotalF4 : '0',
    interest: (account) => account ? account.interestTotalF4 : '0',
    emi: (account) => account ? account.emiTotalF4 : '0',
    previousEmi: (account) => account ? account.previousMonthEMITotalF4 : '0',
    differenceEMI: (account) => account ? account.emiDifferenceTotalF4 : '0',
    intOnOverdueInt: (account) => account ? account.intOnOverdueIntTotalF4 : '0',
    intOnOverdueIntCurrent: (account) => account ? account.intOnOverdueIntTotalCurrentF4 : '0',
    intOnOverduePrincipal: (account) => account ? account.intOnOverduePrincipalTotalF4 : '0',
    intOnOverduePrincipalCurrent: (account) => account ? account.intOnOverduePrincipalTotalCurrentF4 : '0',
    overduePrincipal: (account) => account ? account.overduePrincipalTotalF4 : '0',
    overdueInterest: (account) => account ? account.overdueInterestTotalF4 : '0',
    gst: (account) => account ? account.gstTotalF4 : '0',
    defaultInt: (account) => account ? account.defaultIntTotalF4 : '0',
    overdueDefaultInt: (account) => account ? account.overdueDefaultIntTotalF4 : '0',
    oope: (account) => account ? account.oopeTotalF4 : '0',
    overdueOOPE: (account) => account ? account.overdueOOPETotalF4 : '0',
    intOnOverdueOOPE: (account) => account ? account.intOnOverdueOOPETotalF4 : '0',
    intOnOverdueOOPECurrent: (account) => account ? account.intOnOverdueOOPETotalCurrentF4 : '0',
    tds: (account) => account ? account.tdsTotalF4 : '0',
    totalOverdue: (account) => account ? account.totalOverdueAmountF4 : '0',
    totalCurrentMonthDue: (account) => account ? account.totalCurrentMonthDueAmountF4 : '0',
    previousMonthDues: (account) => account ? account.previousMonthCurrentDemand : '0',
    reconciliation: (account) => account ? account.reconciliationTotalF4 : '0',
    // Missing: allocation
}

const LOAN_TOTAL_FIELD_RESOLVERS = { // May Need sorting
    dsra: (loan) => loan ? loan.dsraF4 : '0',
    principal: (loan) => loan ? loan.principalF4 : '0',
    interest: (loan) => loan ? loan.interestF4 : '0',
    emi: (loan) => loan ? loan.emiF4 : '0',
    previousEmi: (loan) => loan ? loan.previousMonthEMIF4 : '0',
    differenceEMI: (loan) => loan ? loan.emiDifferenceF4 : '0',
    intOnOverduePrincipal: (loan) => loan ? loan.intOnOverduePrincipalF4 : '0',
    intOnOverduePrincipalCurrent: (loan) => loan ? loan.intOnOverduePrincipalCurrentF4 : '0',
    intOnOverdueInt: (loan) => loan ? loan.intOnOverdueIntF4 : '0',
    intOnOverdueIntCurrent: (loan) => loan ? loan.intOnOverdueIntCurrentF4 : '0',
    overduePrincipal: (loan) => loan ? loan.overduePrincipalF4 : '0',
    overdueInterest: (loan) => loan ? loan.overdueInterestF4 : '0',
    gst: (loan) => loan ? loan.gstF4 : '0',
    defaultInt: (loan) => loan ? loan.defaultIntF4 : '0',
    overdueDefaultInt: (loan) => loan ? loan.overdueDefaultIntF4 : '0',
    oope: (loan) => loan ? loan.oopeF4 : '0',
    overdueOOPE: (loan) => loan ? loan.overdueOOPEF4 : '0',
    intOnOverdueOOPE: (loan) => loan ? loan.intOnOverdueOOPEF4 : '0',
    intOnOverdueOOPECurrent: (loan) => loan ? loan.intOnOverdueOOPECurrentF4 : '0',
    tds: (loan) => loan ? loan.tdsF4 : '0',
    toCollect: (loan) => loan ? loan.toCollectF4 : '0',
    previousMonthToCollect: (loan) => loan ? loan.toCollectPreviousMonthF4 : '0',
    differenceToCollect: (loan) => loan ? loan.toCollectDifferenceF4 : '0',
    reconciliation: (loan) => loan ? loan.reconciliationF4 : '0',
    totalOverdue: (loan) => loan ? loan.overdueAmountF4 : '0',
    totalCurrentMonthDue: (loan) => loan ? loan.currentMonthDueAmountF4 : '0',
    totalExpectedCollection: (loan) => loan ? loan.expectedCollectionAmountF4 : '0'
}

const MONTHLY_REPAYMENT_COLUMN_KEYS = [
    'nach',
    'dsraCurrent',
    'dsraAfterCollection',
    'toCollect',
    'previousMonthToCollect',
    'differenceToCollect',
    'reasonToCollect',
    'totalExpectedCollection',
    'previousMonthCollections',
    'diffCurrentMonthDueVsExpectedCollection',
    'paymentInitiatedDifference',
    'dsra',
    'surplus',
    'escc',
    'paymentInitiated',
    'collectionsFromSurplus',
    'collectionsFromDSRA',
    'collectionsFromNACH',
    'principal',
    'interest',
    'emi',
    'previousEmi',
    'differenceEMI',
    'intOnOverdueInt',
    'intOnOverdueIntCurrent',
    'intOnOverduePrincipal',
    'intOnOverduePrincipalCurrent',
    'overduePrincipal',
    'overdueInterest',
    'gst',
    'defaultInt',
    'overdueDefaultInt',
    'oope',
    'overdueOOPE',
    'intOnOverdueOOPE',
    'intOnOverdueOOPECurrent',
    'tds',
    'totalOverdue',
    'totalCurrentMonthDue',
    'previousMonthDues',
    'reconciliation',
    'allocation'
]

const AccountDisbursementUI = ({ context, accountList, totals, type, checked, view, yearMonth, onReasonUpdateClick, onAllocationUpdateClick, onCollectionEMI, showCollectionConfirm, setShowCollectionConfirm, showApprovePayablesModal, setShowApprovePayablesModal, onApprovePayables, onSaveEMITracker, onSaveAndApprove, saveAndApproveStep, saveLoading, collectionLoading, approveLoading, columnVisibility, setColumnVisibility, auditLogsResponse }) => {
    const isEmiTrackerSaved = !!(auditLogsResponse && auditLogsResponse.emiTrackerSavedDateISO)
    const formatDate = (date) => {
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}-${month < 10 ? '0' : ''}${month}-${year}`;
    };

    const isLoanLevelView = !!(checked || view)

    // Column configuration to check if all columns are visible
    const columnConfig = MONTHLY_REPAYMENT_COLUMN_KEYS

    // Check if all columns are visible
    const allColumnsVisible = columnConfig.every(key => columnVisibility && columnVisibility[key])
    // Check if at least one column is visible (to show filter row)
    const atLeastOneColumnVisible = columnConfig.some(key => columnVisibility && columnVisibility[key])
    const [columnOrder, setColumnOrder] = useState(MONTHLY_REPAYMENT_COLUMN_KEYS)
    const [draggedColumnKey, setDraggedColumnKey] = useState(null)

    useEffect(() => {
        setColumnOrder(prevOrder => {
            const validOrdered = prevOrder.filter(key => MONTHLY_REPAYMENT_COLUMN_KEYS.includes(key))
            const missing = MONTHLY_REPAYMENT_COLUMN_KEYS.filter(key => !validOrdered.includes(key))
            return [...validOrdered, ...missing]
        })
    }, [])

    const orderedVisibleColumns = useMemo(
        () => columnOrder.filter(key => columnVisibility && columnVisibility[key]),
        [columnOrder, columnVisibility]
    )

    const parseAmount = (value) => {
        if (value === null || value === undefined) return 0
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string') {
            const trimmed = value.trim()
            if (!trimmed) return 0
            const cleaned = trimmed.replace(/,/g, '').replace(/[^\d.-]/g, '')
            const parsed = parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : 0
        }
        if (typeof value === 'object' && value !== null && 'toString' in value) {
            return parseAmount(value.toString())
        }
        return 0
    }

    const formatAmount = (value) => {
        const numeric = Number.isFinite(value) ? value : parseAmount(value)
        const safeNumeric = Number.isFinite(numeric) ? numeric : 0
        return safeNumeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const [filters, setFilters] = useState({
        accountLoan: '',
        nach: '',
        dsra: '',
        toCollect: '',
        previousMonthToCollect: '',
        differenceToCollect: '',
        reasonToCollect: '',
        totalExpectedCollection: '',
        previousMonthCollections: '',
        diffCurrentMonthDueVsExpectedCollection: '',
        paymentInitiated: '',
        paymentInitiatedDifference: '',
        dsraCurrent: '',
        dsraAfterCollection: '',
        surplus: '',
        escc: '',
        collectionsFromSurplus: '',
        collectionsFromDSRA: '',
        collectionsFromNACH: '',
        principal: '',
        interest: '',
        emi: '',
        previousEmi: '',
        differenceEMI: '',
        intOnOverdueInt: '',
        intOnOverdueIntCurrent: '',
        intOnOverduePrincipal: '',
        intOnOverduePrincipalCurrent: '',
        overduePrincipal: '',
        overdueInterest: '',
        gst: '',
        defaultInt: '',
        overdueDefaultInt: '',
        oope: '',
        overdueOOPE: '',
        intOnOverdueOOPE: '',
        intOnOverdueOOPECurrent: '',
        tds: '',
        totalOverdue: '',
        totalCurrentMonthDue: '',
        previousMonthDues: '',
        reconciliation: '',
        allocation: '',
    })

    const setFilterValue = (name) => (event) => {
        const value = (event && event.target && typeof event.target.value !== 'undefined') ? event.target.value : ''
        setFilters((prev) => ({ ...prev, [name]: value }))
    }

    const getCellValue = (a, key) => {
        switch (key) {
            case 'accountLoan': return a.accountName
            case 'nach': return a.nachTransfer ? 'Active' : 'Inactive'
            case 'dsra': return a.dsraTotalF4
            case 'toCollect': return a.toCollectTotalF4
            case 'previousMonthToCollect': return a.toCollectPreviousMonthTotalF4
            case 'differenceToCollect': return a.toCollectDifferenceTotalF4
            case 'reasonToCollect': return a.reasonForToCollectDifference
            case 'totalExpectedCollection': return a.totalExpectedCollectionAmountF4
            case 'previousMonthCollections': return a.previousMonthPaymentInitiated
            case 'diffCurrentMonthDueVsExpectedCollection': return getFirstAvailableValue(a.diffCurrentMonthDueVsExpectedCollectionTotalF4, a.diffCurrentMonthDueVsExpectedCollection)
            case 'paymentInitiated': return a.totalPaymentInitiatedAmountF4
            case 'paymentInitiatedDifference':
                return (
                    parseAmount(a.totalExpectedCollectionAmountF4 || 0) -
                    parseAmount(a.totalPaymentInitiatedAmountF4 || 0)
                ).toFixed(2)
            case 'dsraCurrent': return a.dsraCurrentTotalF4
            case 'dsraAfterCollection': return a.dsraAfterCollectionTotalF4 || '0'
            case 'surplus': return getFirstAvailableValue(a.surplusBalanceF4, a.surplusBalanceTotalF4)
            case 'escc': return getFirstAvailableValue(a.esccBalanceF4, a.esccBalanceTotalF4)
            case 'collectionsFromSurplus': return getFirstAvailableValue(a.totalCollectionsFromSurplusBalanceTotalF4, a.totalCollectionsFromSurplusBalance)
            case 'collectionsFromDSRA': return getFirstAvailableValue(a.totalCollectionsFromDSRABalanceTotalF4, a.totalCollectionsFromDSRABalance)
            case 'collectionsFromNACH': return getFirstAvailableValue(a.totalCollectionsFromNACHBalanceTotalF4, a.totalCollectionsFromNACHBalance)
            case 'principal': return a.principalTotalF4
            case 'interest': return a.interestTotalF4
            case 'emi': return a.emiTotalF4
            case 'previousEmi': return a.previousMonthEMITotalF4
            case 'differenceEMI': return a.emiDifferenceTotalF4
            case 'intOnOverdueInt': return a.intOnOverdueIntTotalF4
            case 'intOnOverdueIntCurrent': return a.intOnOverdueIntTotalCurrentF4
            case 'intOnOverduePrincipal': return a.intOnOverduePrincipalTotalF4
            case 'intOnOverduePrincipalCurrent': return a.intOnOverduePrincipalTotalCurrentF4
            case 'overduePrincipal': return a.overduePrincipalTotalF4
            case 'overdueInterest': return a.overdueIntTotalF4
            case 'gst': return a.gstTotalF4
            case 'defaultInt': return a.defaultIntTotalF4
            case 'overdueDefaultInt': return a.overdueDefaultIntTotalF4
            case 'oope': return a.oopeTotalF4
            case 'overdueOOPE': return a.overdueOOPETotalF4
            case 'intOnOverdueOOPE': return a.intOnOverdueOOPEF4
            case 'intOnOverdueOOPECurrent': return a.intOnOverdueOOPETotalCurrentF4
            case 'tds': return a.tdsTotalF4
            case 'totalOverdue': return a.totalOverdueAmountF4
            case 'totalCurrentMonthDue': return a.totalCurrentMonthDueAmountF4
            case 'previousMonthDues': return a.previousMonthCurrentDemand
            case 'reconciliation': return a.reconciliationTotalF4
            case 'allocation': return a.reconciliationAllocation || ''

            default: return ''
        }
    }

    const activeFilters = useMemo(() => {
        return Object.entries(filters)
            .map(([key, value]) => [key, (value || '').toString().trim()])
            .filter(([, value]) => value !== '')
    }, [filters])

    const matchesFilterValue = useCallback((rawValue, key, filterValue) => {
        const normalizedFilter = (filterValue || '').toString().trim()
        if (!normalizedFilter) return true
        const rawString = rawValue === null || rawValue === undefined ? '' : rawValue.toString()

        // Handle NACH filter (Active / Inactive)
        if (key === 'nach') {
            const trimmedRaw = rawString.trim().toLowerCase()
            const trimmedFilter = normalizedFilter.trim().toLowerCase()
            if (!trimmedFilter) return true
            return trimmedRaw === trimmedFilter
        }

        // Handle allocation filter
        if (key === 'allocation') {
            const trimmedRawString = rawString.trim()
            const trimmedFilter = normalizedFilter.trim()
            if (!trimmedFilter) return true // "All" option - show everything

            // Map display value to actual stored value
            const actualStoredValue = ALLOCATION_VALUE_MAP[trimmedFilter] || trimmedFilter

            // Exact case-insensitive match for allocation values
            return trimmedRawString.toLowerCase() === actualStoredValue.toLowerCase()
        }

        if (NUMERIC_COLUMNS.has(key)) {
            const numericExpressionMatch = normalizedFilter.match(/^(>=?|<=?|=|>|<)\s*(\d+(?:\.\d+)?)$/)
            if (numericExpressionMatch) {
                const [, operator, value] = numericExpressionMatch
                const numericValue = parseFloat(value)
                const cellNumericValue = parseAmount(rawString)

                switch (operator) {
                    case '>':
                        return cellNumericValue > numericValue
                    case '>=':
                        return cellNumericValue >= numericValue
                    case '<':
                        return cellNumericValue < numericValue
                    case '<=':
                        return cellNumericValue <= numericValue
                    case '=':
                        return Math.abs(cellNumericValue - numericValue) < 0.01
                    default:
                        break
                }
            }
        }

        return rawString.toLowerCase().includes(normalizedFilter.toLowerCase())
    }, [parseAmount])

    const passesFilters = useCallback((account) => {
        if (activeFilters.length === 0) return true

        const accountMatchesAll = activeFilters.every(([key, value]) => matchesFilterValue(getCellValue(account, key), key, value))
        if (accountMatchesAll) return true

        if (!isLoanLevelView) return false

        const accountOnlyFailed = activeFilters.some(([key, value]) =>
            ACCOUNT_ONLY_FILTER_KEYS.has(key) && !matchesFilterValue(getCellValue(account, key), key, value)
        )
        if (accountOnlyFailed) return false

        const loans = Array.isArray(account.suLoanEMITrackerList) ? account.suLoanEMITrackerList : []
        if (loans.length === 0) return false

        return loans.some(loan => {
            return activeFilters.every(([key, value]) => {
                if (ACCOUNT_ONLY_FILTER_KEYS.has(key)) return true
                const loanValue = getLoanFilterValue(loan, key)
                if (loanValue === undefined) return false
                return matchesFilterValue(loanValue, key, value)
            })
        })
    }, [activeFilters, isLoanLevelView, matchesFilterValue])

    const getVisibleLoans = useCallback((account) => {
        if (!isLoanLevelView) return []
        const loans = Array.isArray(account.suLoanEMITrackerList) ? account.suLoanEMITrackerList : []
        if (loans.length === 0) return []
        if (activeFilters.length === 0) return loans

        return loans.filter(loan => activeFilters.every(([key, value]) => {
            if (ACCOUNT_ONLY_FILTER_KEYS.has(key)) return true
            const loanValue = getLoanFilterValue(loan, key)
            if (loanValue === undefined) return false
            return matchesFilterValue(loanValue, key, value)
        }))
    }, [activeFilters, isLoanLevelView, matchesFilterValue])

    // Calculate account totals from visible loans when filters are active
    const getAccountTotalsFromVisibleLoans = useCallback((account) => {
        if (!isLoanLevelView || activeFilters.length === 0) {
            // No filters or not in loan view - return account-level totals
            return {
                nach: account.nachTransfer ? account.emiTotalF4 : '0',
                dsra: account.dsraTotalF4 || '0',
                toCollect: account.toCollectTotalF4 || '0',
                previousMonthToCollect: account.toCollectPreviousMonthTotalF4 || '0',
                differenceToCollect: account.toCollectDifferenceTotalF4 || '0',
                // reasonForDifference: Missing in this snippet
                totalExpectedCollection: account.totalExpectedCollectionAmountF4 || '0',
                previousMonthCollections: account.previousMonthPaymentInitiatedTotalF4 || '0',
                diffCurrentMonthDueVsExpectedCollection: account.diffCurrentMonthDueVsExpectedCollectionTotalF4 || '0',
                paymentInitiated: account.totalPaymentInitiatedAmountF4 || '0',
                paymentInitiatedDifference: (
                    parseAmount(account.totalExpectedCollectionAmountF4 || 0) -
                    parseAmount(account.totalPaymentInitiatedAmountF4 || 0)
                ).toFixed(2),
                dsraCurrent: account.dsraCurrentTotalF4 || '0',
                dsraAfterCollection: account.dsraAfterCollectionTotalF4 || '0',
                surplus: getFirstAvailableValue(account.surplusBalanceF4, account.surplusBalanceTotalF4) || '0',
                escc: getFirstAvailableValue(account.esccBalanceF4, account.esccBalanceTotalF4) || '0',
                collectionsFromSurplus: account.totalCollectionsFromSurplusBalanceTotalF4 || '0',
                collectionsFromDSRA: account.totalCollectionsFromDSRABalanceTotalF4 || '0',
                collectionsFromNACH: account.totalCollectionsFromNACHBalanceTotalF4 || '0',
                principal: account.principalTotalF4 || '0',
                interest: account.interestTotalF4 || '0',
                emi: account.emiTotalF4 || '0',
                previousEmi: account.previousMonthEMITotalF4 || '0',
                differenceEMI: account.emiDifferenceTotalF4 || '0',
                intOnOverdueInt: account.intOnOverdueIntTotalF4 || '0',
                intOnOverdueIntCurrent: account.intOnOverdueIntTotalCurrentF4 || '0',
                intOnOverduePrincipal: account.intOnOverduePrincipalTotalF4 || '0',
                intOnOverduePrincipalCurrent: account.intOnOverduePrincipalTotalCurrentF4 || '0',
                overduePrincipal: account.overduePrincipalTotalF4 || '0',
                overdueInterest: account.overdueInterestTotalF4 || '0',
                gst: account.gstTotalF4 || '0',
                defaultInt: account.defaultIntTotalF4 || '0',
                overdueDefaultInt: account.overdueDefaultIntTotalF4 || '0',
                oope: account.oopeTotalF4 || '0',
                overdueOOPE: account.overdueOOPETotalF4 || '0',
                intOnOverdueOOPE: account.intOnOverdueOOPETotalF4 || '0',
                intOnOverdueOOPECurrent: account.intOnOverdueOOPETotalCurrentF4 || '0',
                tds: account.tdsTotalF4 || '0',
                totalOverdue: account.totalOverdueAmountF4 || '0',
                totalCurrentMonthDue: account.totalCurrentMonthDueAmountF4 || '0',
                previousMonthDues: account.previousMonthCurrentDemandTotalF4 || '0',
                reconciliation: account.reconciliationTotalF4 || '0',
                // allocation: Missing in this snippet
            }
        }

        const visibleLoans = getVisibleLoans(account)
        if (visibleLoans.length === 0) {
            // No visible loans - return zeros
            return {
                nach: '0',
                dsra: '0',
                toCollect: '0',
                previousMonthToCollect: '0',
                differenceToCollect: '0',
                // reasonForDifference: Missing in this snippet
                totalExpectedCollection: '0',
                previousMonthCollections: account.previousMonthPaymentInitiated || '0',
                diffCurrentMonthDueVsExpectedCollection: account.diffCurrentMonthDueVsExpectedCollection || '0',
                paymentInitiated: '0',
                paymentInitiatedDifference: '0',
                dsraCurrent: account.dsraCurrentTotalF4 || '0', // Account-level field
                dsraAfterCollection: account.dsraAfterCollectionTotalF4 || '0',
                surplus: getFirstAvailableValue(account.surplusBalanceF4, account.surplusBalanceTotalF4) || '0', // Account-level field
                escc: getFirstAvailableValue(account.esccBalanceF4, account.esccBalanceTotalF4) || '0', // Account-level field
                collectionsFromSurplus: getFirstAvailableValue(account.totalCollectionsFromSurplusBalanceTotalF4, account.totalCollectionsFromSurplusBalance) || '0',
                collectionsFromDSRA: getFirstAvailableValue(account.totalCollectionsFromDSRABalanceTotalF4, account.totalCollectionsFromDSRABalance) || '0',
                collectionsFromNACH: getFirstAvailableValue(account.totalCollectionsFromNACHBalanceTotalF4, account.totalCollectionsFromNACHBalance) || '0',
                principal: '0',
                interest: '0',
                emi: '0',
                previousEmi: '0',
                differenceEMI: '0',
                intOnOverdueInt: '0',
                intOnOverdueIntCurrent: '0',
                intOnOverduePrincipal: '0',
                intOnOverduePrincipalCurrent: '0',
                overduePrincipal: '0',
                overdueInterest: '0',
                gst: '0',
                defaultInt: '0',
                overdueDefaultInt: '0',
                oope: '0',
                overdueOOPE: '0',
                intOnOverdueOOPE: '0',
                intOnOverdueOOPECurrent: '0',
                tds: '0',
                totalOverdue: '0',
                totalCurrentMonthDue: '0',
                previousMonthDues: account.previousMonthCurrentDemand || '0',
                reconciliation: '0',
                // allocation: Missing in this snippet
            }
        }

        // Sum up values from visible loans
        const totals = {
            nach: account.nachTransfer ? account.emiTotalF4 : '0', // Account-level field
            dsra: '0',
            toCollect: '0',
            previousMonthToCollect: '0',
            differenceToCollect: '0',
            // reasonForDifference: Missing in this snippet
            totalExpectedCollection: '0',
            previousMonthCollections: account.previousMonthPaymentInitiated || '0',
            diffCurrentMonthDueVsExpectedCollection: account.diffCurrentMonthDueVsExpectedCollection || '0',
            paymentInitiated: account.totalPaymentInitiatedAmountF4 || '0',
            paymentInitiatedDifference: (
                parseAmount(account.totalExpectedCollectionAmountF4 || 0) -
                parseAmount(account.totalPaymentInitiatedAmountF4 || 0)
            ).toFixed(2),
            dsraCurrent: account.dsraCurrentTotalF4 || '0', // Account-level field
            dsraAfterCollection: account.dsraAfterCollectionTotalF4 || '0',
            surplus: getFirstAvailableValue(account.surplusBalanceF4, account.surplusBalanceTotalF4) || '0', // Account-level field
            escc: getFirstAvailableValue(account.esccBalanceF4, account.esccBalanceTotalF4) || '0', // Account-level field
            collectionsFromSurplus: getFirstAvailableValue(account.totalCollectionsFromSurplusBalanceTotalF4, account.totalCollectionsFromSurplusBalance) || '0',
            collectionsFromDSRA: getFirstAvailableValue(account.totalCollectionsFromDSRABalanceTotalF4, account.totalCollectionsFromDSRABalance) || '0',
            collectionsFromNACH: getFirstAvailableValue(account.totalCollectionsFromNACHBalanceTotalF4, account.totalCollectionsFromNACHBalance) || '0',
            principal: '0',
            interest: '0',
            emi: '0',
            previousEmi: '0',
            differenceEMI: '0',
            intOnOverdueInt: '0',
            intOnOverdueIntCurrent: '0',
            intOnOverduePrincipal: '0',
            intOnOverduePrincipalCurrent: '0',
            overduePrincipal: '0',
            overdueInterest: '0',
            gst: '0',
            defaultInt: '0',
            overdueDefaultInt: '0',
            oope: '0',
            overdueOOPE: '0',
            intOnOverdueOOPE: '0',
            intOnOverdueOOPECurrent: '0',
            tds: '0',
            totalOverdue: '0',
            totalCurrentMonthDue: '0',
            previousMonthDues: account.previousMonthCurrentDemand || '0',
            reconciliation: '0',
            // allocation: Missing in this snippet
        }

        visibleLoans.forEach(loan => {
            if (loan.dsraF4) totals.dsra = (parseAmount(totals.dsra) + parseAmount(loan.dsraF4)).toFixed(2)
            if (loan.toCollectF4) totals.toCollect = (parseAmount(totals.toCollect) + parseAmount(loan.toCollectF4)).toFixed(2)
            if (loan.toCollectPreviousMonthF4) totals.previousMonthToCollect = (parseAmount(totals.previousMonthToCollect) + parseAmount(loan.toCollectPreviousMonthF4)).toFixed(2)
            if (loan.toCollectDifferenceF4) totals.differenceToCollect = (parseAmount(totals.differenceToCollect) + parseAmount(loan.toCollectDifferenceF4)).toFixed(2)
            // Missing: reasonForDifference logic
            if (loan.expectedCollectionAmountF4) totals.totalExpectedCollection = (parseAmount(totals.totalExpectedCollection) + parseAmount(loan.expectedCollectionAmountF4)).toFixed(2)
            // Missing: previousMonthCollections, diffCurrentVsProposed, paymentInitiated logic
            if (loan.dsraCurrentF4) totals.dsraCurrent = (parseAmount(totals.dsraCurrent) + parseAmount(loan.dsraCurrentF4)).toFixed(2)
            if (loan.dsraAfterCollectionF4) totals.dsraAfterCollection = (parseAmount(totals.dsraAfterCollection) + parseAmount(loan.dsraAfterCollectionF4)).toFixed(2)
            if (loan.principalF4) totals.principal = (parseAmount(totals.principal) + parseAmount(loan.principalF4)).toFixed(2)
            if (loan.interestF4) totals.interest = (parseAmount(totals.interest) + parseAmount(loan.interestF4)).toFixed(2)
            if (loan.emiF4) totals.emi = (parseAmount(totals.emi) + parseAmount(loan.emiF4)).toFixed(2)
            if (loan.previousMonthEMIF4) totals.previousEmi = (parseAmount(totals.previousEmi) + parseAmount(loan.previousMonthEMIF4)).toFixed(2)
            if (loan.emiDifferenceF4) totals.differenceEMI = (parseAmount(totals.differenceEMI) + parseAmount(loan.emiDifferenceF4)).toFixed(2)
            if (loan.intOnOverdueIntF4) totals.intOnOverdueInt = (parseAmount(totals.intOnOverdueInt) + parseAmount(loan.intOnOverdueIntF4)).toFixed(2)
            if (loan.intOnOverdueIntCurrentF4) totals.intOnOverdueIntCurrent = (parseAmount(totals.intOnOverdueIntCurrent) + parseAmount(loan.intOnOverdueIntCurrentF4)).toFixed(2)
            if (loan.intOnOverduePrincipalF4) totals.intOnOverduePrincipal = (parseAmount(totals.intOnOverduePrincipal) + parseAmount(loan.intOnOverduePrincipalF4)).toFixed(2)
            if (loan.intOnOverduePrincipalCurrentF4) totals.intOnOverduePrincipalCurrent = (parseAmount(totals.intOnOverduePrincipalCurrent) + parseAmount(loan.intOnOverduePrincipalCurrentF4)).toFixed(2)
            if (loan.overduePrincipalF4) totals.overduePrincipal = (parseAmount(totals.overduePrincipal) + parseAmount(loan.overduePrincipalF4)).toFixed(2)
            if (loan.overdueInterestF4) totals.overdueInterest = (parseAmount(totals.overdueInterest) + parseAmount(loan.overdueInterestF4)).toFixed(2)
            if (loan.gstF4) totals.gst = (parseAmount(totals.gst) + parseAmount(loan.gstF4)).toFixed(2)
            if (loan.defaultIntF4) totals.defaultInt = (parseAmount(totals.defaultInt) + parseAmount(loan.defaultIntF4)).toFixed(2)
            if (loan.overdueDefaultIntF4) totals.overdueDefaultInt = (parseAmount(totals.overdueDefaultInt) + parseAmount(loan.overdueDefaultIntF4)).toFixed(2)
            if (loan.oopeF4) totals.oope = (parseAmount(totals.oope) + parseAmount(loan.oopeF4)).toFixed(2)
            if (loan.overdueOOPEF4) totals.overdueOOPE = (parseAmount(totals.overdueOOPE) + parseAmount(loan.overdueOOPEF4)).toFixed(2)
            if (loan.intOnOverdueOOPEF4) totals.intOnOverdueOOPE = (parseAmount(totals.intOnOverdueOOPE) + parseAmount(loan.intOnOverdueOOPEF4)).toFixed(2)
            if (loan.intOnOverdueOOPECurrentF4) totals.intOnOverdueOOPECurrent = (parseAmount(totals.intOnOverdueOOPECurrent) + parseAmount(loan.intOnOverdueOOPECurrentF4)).toFixed(2)
            if (loan.tdsF4) totals.tds = (parseAmount(totals.tds) + parseAmount(loan.tdsF4)).toFixed(2)
            if (loan.overdueAmountF4) totals.totalOverdue = (parseAmount(totals.totalOverdue) + parseAmount(loan.overdueAmountF4)).toFixed(2)
            if (loan.currentMonthDueAmountF4) totals.totalCurrentMonthDue = (parseAmount(totals.totalCurrentMonthDue) + parseAmount(loan.currentMonthDueAmountF4)).toFixed(2)
            // Missing: previousMonthDues logic
            if (loan.reconciliationF4) totals.reconciliation = (parseAmount(totals.reconciliation) + parseAmount(loan.reconciliationF4)).toFixed(2)
        })

        return totals
    }, [isLoanLevelView, activeFilters, getVisibleLoans, parseAmount])

    const filteredByType = useMemo(
        () => (accountList || []).filter(a => a.accountType === type).filter(passesFilters),
        [accountList, type, passesFilters]
    )

    const computedTotals = useMemo(() => {
        const accumulator = TOTAL_FIELD_KEYS.reduce((acc, key) => {
            acc[key] = 0
            return acc
        }, {})

        filteredByType.forEach(account => {
            if (!account) return

            accumulator.nach += parseAmount(ACCOUNT_TOTAL_FIELD_RESOLVERS.nach(account))

            const loansForTotals = getVisibleLoans(account)

            TOTAL_FIELD_KEYS_WITHOUT_NACH.forEach(key => {
                const accountResolver = ACCOUNT_TOTAL_FIELD_RESOLVERS[key]
                if (
                    isLoanLevelView &&
                    LOAN_TOTAL_FIELD_RESOLVERS[key] &&
                    loansForTotals && loansForTotals.length > 0
                ) {
                    let loanSum = 0
                    loansForTotals.forEach(loan => {
                        loanSum += parseAmount(LOAN_TOTAL_FIELD_RESOLVERS[key](loan))
                    })
                    accumulator[key] += loanSum
                } else if (accountResolver) {
                    accumulator[key] += parseAmount(accountResolver(account))
                }
            })
        })

        return accumulator
    }, [filteredByType, isLoanLevelView, getVisibleLoans])

    const grandExpectedCollectionTotal = parseAmount(
        totals && (type === 'individual' ? totals.grandExpectedCollectionsAmountTotalRF4 : totals.grandExpectedCollectionsAmountTotalF4)
    )
    const grandPaymentInitiatedTotal = parseAmount(
        totals && (type === 'individual' ? totals.grandPaymentInitiatedRF4 : totals.grandPaymentInitiatedF4)
    )
    const grandPaymentInitiatedDifferenceTotal = grandExpectedCollectionTotal - grandPaymentInitiatedTotal

    const tableWrapperRef = useRef(null)
    const [scrollPct, setScrollPct] = useState(0)
    const onScrollSliderChange = (event) => {
        const pct = parseInt(event.target.value || '0')
        setScrollPct(pct)
        const el = tableWrapperRef.current
        if (el) {
            const maxScroll = el.scrollWidth - el.clientWidth
            el.scrollLeft = Math.round(maxScroll * (pct / 100))
        }
    }

    const onWrapperScroll = () => {
        const el = tableWrapperRef.current
        if (!el) return
        const maxScroll = el.scrollWidth - el.clientWidth
        const pct = maxScroll > 0 ? Math.round((el.scrollLeft / maxScroll) * 100) : 0
        if (pct !== scrollPct) setScrollPct(pct)
    }

    const topStickyStyle = { position: 'sticky', top: 0, background: '#fff', zIndex: 100 };
    // Filter row should stick below the first header row (approximately 50-60px height)
    const filterRowTop = 60; // Adjust this value based on actual first row height
    const filterRowStickyStyle = { position: 'sticky', top: filterRowTop, background: '#fff', zIndex: 99 };
    const tableColumnCount = 2 + orderedVisibleColumns.length

    const getColumnLabel = (key) => { /// May need sorting
        switch (key) {
            case 'dsra': return 'DSRA Required'
            case 'dsraCurrent': return <>Dsra Before Collection<br /><span className='g_small g_muted'>(as on {formatDate(new Date())})</span></>
            case 'dsraAfterCollection': return 'DSRA After Collection'
            case 'previousEmi': return 'Previous EMI'
            case 'differenceEMI': return 'EMI Difference'
            case 'intOnOverduePrincipal': return 'Int On Overdue Principal'
            case 'intOnOverdueInt': return 'Int On Overdue Int'
            case 'overduePrincipal': return 'Overdue Principal'
            case 'overdueInterest': return 'Overdue Interest'
            case 'gst': return 'GST'
            case 'oope': return 'OOPE'
            case 'overdueOOPE': return 'Overdue OOPE'
            case 'intOnOverdueOOPE': return 'Int On Overdue OOPE'
            case 'tds': return 'TDS'
            case 'previousMonthToCollect': return 'Previous Month To Collect'
            case 'differenceToCollect': return 'To Collect Difference'
            case 'escc': return 'ESCC Balance'
            case 'surplus': return 'Surplus Balance'
            case 'paymentInitiated': return 'Payment Initiated'
            case 'reconciliation': return 'Reconciliation'
            case 'toCollect': return 'To Collect'
            case 'totalCurrentMonthDue': return 'Current Dues'
            case 'collectionsFromSurplus': return 'Collections from Surplus'
            case 'collectionsFromDSRA': return 'Collections from DSRA'
            case 'collectionsFromNACH': return 'Collections from NACH'
            case 'previousMonthDues': return 'Previous Month Dues'
            case 'previousMonthCollections': return 'Previous Month Collections'
            case 'diffCurrentMonthDueVsExpectedCollection': return 'Difference b/w Current Dues and Proposed Collections'
            case 'totalExpectedCollection': return 'Total Proposed Collections'
            case 'reasonToCollect': return 'Reason for Difference'
            case 'defaultInt': return 'Penal Charge'
            case 'overdueDefaultInt': return 'Overdue Penal Charge'
            case 'intOnOverduePrincipalCurrent': return 'Int on overdue Principal (Current)'
            case 'intOnOverdueIntCurrent': return 'Int on overdue Interest (Current)'
            case 'intOnOverdueOOPECurrent': return 'Int on overdue OOPE (Current)'
            case 'totalOverdue': return 'Total Overdue'
            case 'paymentInitiatedDifference': return 'Difference'
            case 'nach': return 'Nach'
            case 'allocation': return 'Allocation'
            default:
                return (columnConfig.find((colKey) => colKey === key) && key)
                    ? key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, c => c.toUpperCase())
                    : key
        }
    }

    const onColumnDragStart = (key) => (event) => {
        setDraggedColumnKey(key)
        if (event && event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', key)
        }
    }

    const onColumnDragOver = (event) => {
        event.preventDefault()
        if (event && event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move'
        }
    }

    const onColumnDrop = (targetKey) => (event) => {
        event.preventDefault()
        const sourceKey = draggedColumnKey || (event && event.dataTransfer ? event.dataTransfer.getData('text/plain') : null)
        if (!sourceKey || sourceKey === targetKey) return
        setColumnOrder(prevOrder => {
            const sourceIndex = prevOrder.indexOf(sourceKey)
            const targetIndex = prevOrder.indexOf(targetKey)
            if (sourceIndex < 0 || targetIndex < 0) return prevOrder
            const updated = [...prevOrder]
            const [moved] = updated.splice(sourceIndex, 1)
            updated.splice(targetIndex, 0, moved)
            return updated
        })
        setDraggedColumnKey(null)
    }

    const getLoanCellValue = (loan, key) => { /// may need sorting
        switch (key) {
            case 'dsra': return loan.dsraF4
            case 'principal': return loan.principalF4
            case 'interest': return loan.interestF4
            case 'emi': return loan.emiF4
            case 'dsraAfterCollection': return loan.dsraAfterCollectionF4
            case 'previousEmi': return loan.previousMonthEMIF4
            case 'differenceEMI': return loan.emiDifferenceF4
            case 'intOnOverduePrincipal': return loan.intOnOverduePrincipalF4
            case 'intOnOverduePrincipalCurrent': return loan.intOnOverduePrincipalCurrentF4
            case 'intOnOverdueInt': return loan.intOnOverdueIntF4
            case 'intOnOverdueIntCurrent': return loan.intOnOverdueIntCurrentF4
            case 'overduePrincipal': return loan.overduePrincipalF4
            case 'overdueInterest': return loan.overdueInterestF4
            case 'gst': return loan.gstF4
            case 'defaultInt': return loan.defaultIntF4
            case 'overdueDefaultInt': return loan.overdueDefaultIntF4
            case 'oope': return loan.oopeF4
            case 'overdueOOPE': return loan.overdueOOPEF4
            case 'intOnOverdueOOPE': return loan.intOnOverdueOOPEF4
            case 'intOnOverdueOOPECurrent': return loan.intOnOverdueOOPECurrentF4
            case 'tds': return loan.tdsF4
            case 'toCollect': return loan.toCollectF4
            case 'previousMonthToCollect': return loan.toCollectPreviousMonthF4
            case 'differenceToCollect': return loan.toCollectDifferenceF4
            case 'totalOverdue': return loan.overdueAmountF4
            case 'totalCurrentMonthDue': return loan.currentMonthDueAmountF4
            case 'totalExpectedCollection': return loan.expectedCollectionAmountF4
            case 'reconciliation': return loan.reconciliationF4
            default: return ''
        }
    }

    return (
        <div>
            <CardUI title={`Monthly Repayments`} className='g_m_bottom_100'>
                <div ref={tableWrapperRef} onScroll={onWrapperScroll} style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
                    <table className='g_small table'>
                        <thead>
                            {/* Header Row */}
                            <tr className='g_sticky_header'>
                                <th className='g_sticky_header_sno'>S no.</th>
                                <th className='g_sticky_header_account'>Account/Loan</th>
                                {orderedVisibleColumns.map((key) => (
                                    <th
                                        key={`head-${key}`}
                                        draggable
                                        onDragStart={onColumnDragStart(key)}
                                        onDragOver={onColumnDragOver}
                                        onDrop={onColumnDrop(key)}
                                        title='Drag to reorder column'
                                        style={{ cursor: 'move' }}
                                    >
                                        {getColumnLabel(key)}
                                    </th>
                                ))}
                            </tr>

                            {atLeastOneColumnVisible && (
                                <tr className='head'>
                                    <th className='g_sticky_filter_sno'></th>
                                    <th className='g_p_left_100 g_sticky_filter_account'><input className='g_input g_small' placeholder='Filter' value={filters.accountLoan || ''} onChange={setFilterValue('accountLoan')} /></th>
                                    {orderedVisibleColumns.map((key) => {
                                        if (key === 'nach') {
                                            return (
                                                <th key={`filter-${key}`} className='g_p_left_100'>
                                                    <select className='g_input g_small' value={filters.nach || ''} onChange={setFilterValue('nach')}>
                                                        <option value=''>All</option>
                                                        <option value='Active'>Active</option>
                                                        <option value='Inactive'>Inactive</option>
                                                    </select>
                                                </th>
                                            )
                                        }
                                        if (key === 'allocation') {
                                            return (
                                                <th key={`filter-${key}`} className='g_p_left_100'>
                                                    <select className='g_input g_small' value={filters.allocation || ''} onChange={setFilterValue('allocation')} style={{ width: '100%' }}>
                                                        <option value=''>All</option>
                                                        <option value='EWS'>EWS</option>
                                                        <option value='NPA'>NPA</option>
                                                        <option value='Expected NACH'>Expected NACH</option>
                                                        <option value='Expected Inflow'>Expected Inflow</option>
                                                    </select>
                                                </th>
                                            )
                                        }
                                        const placeholder = key === 'reasonToCollect' ? 'Filter' : 'Filter (e.g., >0)'
                                        return (
                                            <th key={`filter-${key}`} className='g_p_left_100'>
                                                <input className='g_input g_small' placeholder={placeholder} value={filters[key] || ''} onChange={setFilterValue(key)} />
                                            </th>
                                        )
                                    })}
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {filteredByType && filteredByType.length > 0 && filteredByType.map((account, i) => {
                                const loansToRender = isLoanLevelView ? getVisibleLoans(account) : (Array.isArray(account.suLoanEMITrackerList) ? account.suLoanEMITrackerList : [])
                                const accountTotals = getAccountTotalsFromVisibleLoans(account)
                                const targetYearMonth = yearMonth ? `${yearMonth.year}-${String(yearMonth.month).padStart(2, '0')}` : null
                                const loans = Array.isArray(account.suLoanEMITrackerList) ? account.suLoanEMITrackerList : []
                                const primaryLoan = (targetYearMonth ? loans.find(loan => loan && loan.repaymentYearMonth === targetYearMonth) : null) || loans[0] || null
                                const allocationLoanId = primaryLoan ? `${primaryLoan.loanId}#${account.reconciliationAllocation || ''}` : null
                                const isLoanClosed = (loan) => loan && (loan.closed === true || loan.closed === 'true' || loan.isClosed === true || loan.isClosed === 'true');
                                const areAllLoansClosed = loans.length > 0
                                    ? loans.every(isLoanClosed)
                                    : Boolean(account && (account.closed === true || account.closed === 'true' || account.isClosed === true || account.isClosed === 'true'));
                                return (
                                    <React.Fragment key={`${account.accountName || account.accountId || 'account'}-${i}`}>
                                        {account.accountType === type && (
                                            <tr className={`${account.balanceStatus === 'insufficient_balance' ? 'g_text_danger' :
                                                account.balanceStatus === 'with_dsra_dip' ? 'g_info' :
                                                    account.balanceStatus === 'sufficient_balance' ? 'g_success' : ''} ${areAllLoansClosed ? 'g_closed' : ''}`.trim()}>
                                                <td className='left g_sticky_data_sno'>{i + 1}.</td>
                                                <td className='left g_no_wrap_2 g_sticky_data_account'>{account.accountName}</td>
                                                {orderedVisibleColumns.map((key) => {
                                                    if (key === 'nach') return <td key={`a-${key}`} className='g_text_right amt'>{account.nachTransfer ? 'Active' : 'Inactive'}</td>
                                                    if (key === 'reasonToCollect') {
                                                        return (
                                                            <td key={`a-${key}`} className='g_text_right amt'>
                                                                {account.reasonForToCollectDifference}
                                                                {!isEmiTrackerSaved && primaryLoan && (
                                                                    <span
                                                                        className='icon icon-pencil g_muted g_m_left_50'
                                                                        onClick={onReasonUpdateClick}
                                                                        data-loan-id={primaryLoan.loanId}
                                                                        data-column='toCollect'
                                                                        data-reason={account.reasonForToCollectDifference || ''}
                                                                    />
                                                                )}
                                                            </td>
                                                        )
                                                    }
                                                    if (key === 'allocation') {
                                                        return (
                                                            <td key={`a-${key}`}>
                                                                {account.balanceStatus === 'insufficient_balance' || account.balanceStatus === 'with_dsra_dip' ? (
                                                                    <ButtonTinyUI label={account.reconciliationAllocation ? (account.reconciliationAllocation === 'Default Expected' ? 'NPA' : account.reconciliationAllocation) : '[Allocate]'} onClick={onAllocationUpdateClick} rowId={allocationLoanId} />
                                                                ) : (
                                                                    <span className='g_muted'></span>
                                                                )}
                                                            </td>
                                                        )
                                                    }
                                                    if (key === 'paymentInitiated') {
                                                        return (
                                                            <td key={`a-${key}`} className='g_text_right amt'>
                                                                {account.accountId ? (
                                                                    <Link to={`/su/accounts/${account.accountId}/payables`}>
                                                                        {formatAmount(accountTotals.paymentInitiated)}
                                                                    </Link>
                                                                ) : formatAmount(accountTotals.paymentInitiated)}
                                                            </td>
                                                        )
                                                    }
                                                    return <td key={`a-${key}`} className='g_text_right amt'>{formatAmount(accountTotals[key])}</td>
                                                })}
                                            </tr>
                                        )}
                                        {account.accountType === type && checked && loansToRender && loansToRender.map((loan, j) => {
                                            const isClosed = isLoanClosed(loan);
                                            return (
                                            <tr className={`loan ${isClosed ? 'g_closed' : ''}`} key={`${loan.loanId || loan.loanName || 'loan'}-${j}`}>
                                                <td className='left g_sticky_data_sno'>{i + 1}.{j + 1}</td>
                                                <td className='left g_no_wrap_2 g_sticky_data_account'>
                                                    <Link to={`/su/loans/${loan.loanId}/repayments/v2`} className='g_flex_v'>
                                                        {loan.loanName}
                                                    </Link>
                                                </td>
                                                {orderedVisibleColumns.map((key) => {
                                                    const accountOnlyOnLoan = new Set([
                                                        'nach', 'dsraCurrent', 'escc', 'surplus',
                                                        'collectionsFromSurplus', 'collectionsFromDSRA', 'collectionsFromNACH',
                                                        'previousMonthDues', 'previousMonthCollections', 'diffCurrentMonthDueVsExpectedCollection',
                                                        'paymentInitiated', 'paymentInitiatedDifference', 'allocation', 'reasonToCollect'
                                                    ])
                                                    if (accountOnlyOnLoan.has(key)) return <td key={`l-${key}`} className='g_text_right amt'></td>
                                                    return <td key={`l-${key}`} className='g_text_right amt'>{getLoanCellValue(loan, key)}</td>
                                                })}
                                            </tr>
                                        )})}
                                    </React.Fragment>
                                )
                            })}
                            {filteredByType.length === 0 && (
                                <tr>
                                    <td colSpan={tableColumnCount} className='g_muted g_small'>No accounts found</td>
                                </tr>
                            )}
                            {type === 'business' && allColumnsVisible && (
                                <tr className='foot'>
                                    <td className='left g_sticky_data_sno'></td>
                                    <td className='g_sticky_data_account'><b>Total</b></td>
                                    {orderedVisibleColumns.map((key) => {
                                        if (key === 'nach' || key === 'reasonToCollect' || key === 'allocation') return <td key={`tb-${key}`}></td>
                                        if (key === 'toCollect') return <td key={`tb-${key}`} id='grandToCollectTotal' className='g_text_right amt'><b>{formatAmount(computedTotals.toCollect)}</b></td>
                                        if (key === 'totalOverdue') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandOverdueAmountTotalF4) || computedTotals.totalOverdue)}</b></td>
                                        if (key === 'totalCurrentMonthDue') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandCurrentMonthDueAmountTotalF4) || computedTotals.totalCurrentMonthDue)}</b></td>
                                        if (key === 'collectionsFromSurplus') return <td key={`tb-${key}`} id='grandCollectionsFromSurplus' className='g_text_right amt'><b>{formatAmount((totals && totals.grandTotalCollectionsFromSurplusBalanceTotalF4) || computedTotals.collectionsFromSurplus)}</b></td>
                                        if (key === 'collectionsFromDSRA') return <td key={`tb-${key}`} id='grandCollectionsFromDSRA' className='g_text_right amt'><b>{formatAmount((totals && totals.grandTotalCollectionsFromDSRABalanceTotalF4) || computedTotals.collectionsFromDSRA)}</b></td>
                                        if (key === 'collectionsFromNACH') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandTotalCollectionsFromNACHBalanceTotalF4) || computedTotals.collectionsFromNACH)}</b></td>
                                        if (key === 'previousMonthDues') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandPreviousMonthCurrentDemandTotalF4) || computedTotals.previousMonthDues)}</b></td>
                                        if (key === 'previousMonthCollections') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandPreviousMonthPaymentInitiatedTotalF4) || computedTotals.previousMonthCollections)}</b></td>
                                        if (key === 'diffCurrentMonthDueVsExpectedCollection') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandDiffCurrentMonthDueVsExpectedCollectionTotalF4) || computedTotals.diffCurrentMonthDueVsExpectedCollection)}</b></td>
                                        if (key === 'totalExpectedCollection') return <td key={`tb-${key}`} id='grandExpectedCollectionTotal' className='g_text_right amt'><b>{formatAmount((totals && totals.grandExpectedCollectionsAmountTotalF4) || computedTotals.totalExpectedCollection)}</b></td>
                                        if (key === 'paymentInitiated') return <td key={`tb-${key}`} id='paymentInitiated' className='g_text_right amt'><b>{formatAmount((totals && totals.grandPaymentInitiatedF4) || computedTotals.paymentInitiated)}</b></td>
                                        if (key === 'paymentInitiatedDifference') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals ? grandPaymentInitiatedDifferenceTotal : computedTotals.paymentInitiatedDifference))}</b></td>
                                        if (key === 'dsraAfterCollection') return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandDSRACurrentAfterCollectionF4) || computedTotals.dsraAfterCollection)}</b></td>
                                        return <td key={`tb-${key}`} className='g_text_right amt'><b>{formatAmount(computedTotals[key])}</b></td>
                                    })}
                                </tr>
                            )}
                            {type === 'individual' && allColumnsVisible && (
                                <tr className='foot'>
                                    <td className='left g_sticky_data_sno'></td>
                                    <td className='g_sticky_data_account'><b>Total</b></td>
                                    {orderedVisibleColumns.map((key) => {
                                        if (key === 'reasonToCollect' || key === 'allocation') return <td key={`ti-${key}`}></td>
                                        if (key === 'nach') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount(computedTotals.nach)}</b></td>
                                        if (key === 'toCollect') return <td key={`ti-${key}`} id='grandToCollectTotal' className='g_text_right amt'><b>{formatAmount(computedTotals.toCollect)}</b></td>
                                        if (key === 'totalOverdue') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandOverdueAmountTotalRF4) || computedTotals.totalOverdue)}</b></td>
                                        if (key === 'totalCurrentMonthDue') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandCurrentMonthDueAmountTotalRF4) || computedTotals.totalCurrentMonthDue)}</b></td>
                                        if (key === 'collectionsFromSurplus') return <td key={`ti-${key}`} id='grandCollectionsFromSurplus' className='g_text_right amt'><b>{formatAmount((totals && totals.grandTotalCollectionsFromSurplusBalanceTotalF4) || computedTotals.collectionsFromSurplus)}</b></td>
                                        if (key === 'collectionsFromDSRA') return <td key={`ti-${key}`} id='grandCollectionsFromDSRA' className='g_text_right amt'><b>{formatAmount((totals && totals.grandTotalCollectionsFromDSRABalanceTotalF4) || computedTotals.collectionsFromDSRA)}</b></td>
                                        if (key === 'collectionsFromNACH') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandTotalCollectionsFromNACHBalanceTotalF4) || computedTotals.collectionsFromNACH)}</b></td>
                                        if (key === 'previousMonthDues') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandPreviousMonthCurrentDemandTotalF4) || computedTotals.previousMonthDues)}</b></td>
                                        if (key === 'previousMonthCollections') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandPreviousMonthPaymentInitiatedTotalF4) || computedTotals.previousMonthCollections)}</b></td>
                                        if (key === 'diffCurrentMonthDueVsExpectedCollection') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandDiffCurrentMonthDueVsExpectedCollectionTotalF4) || computedTotals.diffCurrentMonthDueVsExpectedCollection)}</b></td>
                                        if (key === 'totalExpectedCollection') return <td key={`ti-${key}`} id='grandExpectedCollectionTotal' className='g_text_right amt'><b>{formatAmount((totals && totals.grandExpectedCollectionsAmountTotalRF4) || computedTotals.totalExpectedCollection)}</b></td>
                                        if (key === 'paymentInitiated') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandPaymentInitiatedRF4) || computedTotals.paymentInitiated)}</b></td>
                                        if (key === 'paymentInitiatedDifference') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals ? grandPaymentInitiatedDifferenceTotal : computedTotals.paymentInitiatedDifference))}</b></td>
                                        if (key === 'dsraAfterCollection') return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount((totals && totals.grandDSRACurrentAfterCollectionRF4) || computedTotals.dsraAfterCollection)}</b></td>
                                        return <td key={`ti-${key}`} className='g_text_right amt'><b>{formatAmount(computedTotals[key])}</b></td>
                                    })}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className='g_small' style={{ display: 'flex', alignItems: 'center', gap: '30px', marginTop: '25px', justifyContent: 'space-between' }}>
                    {/* {
                        context && context.permissions.includes('su_superuser_business') && totals.collectionInitiated && !totals.payablesApproved &&
                        <button
                            className='g_btn'
                            onClick={() => !(saveLoading || approveLoading) && setShowApprovePayablesModal(true)}
                            disabled={saveLoading || approveLoading}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                border: '1px solid #007bff',
                                background: '#e7f3ff',
                                color: '#0056b3',
                                cursor: (saveLoading || approveLoading) ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                opacity: (saveLoading || approveLoading) ? 0.8 : 1,
                            }}
                        >
                            {(saveLoading || approveLoading) && (
                                <span style={{
                                    width: '14px', height: '14px',
                                    border: '2px solid #0056b3',
                                    borderTopColor: 'transparent',
                                    borderRadius: '50%',
                                    display: 'inline-block',
                                    animation: 'spin 0.7s linear infinite',
                                    flexShrink: 0,
                                }} />
                            )}
                            {saveAndApproveStep === 'saving' ? 'Saving EMI Tracker...' : saveAndApproveStep === 'approving' ? 'Approving Payables...' : 'Save EMI Tracker & Approve Payables'}
                        </button>
                    } */}
                    <div className='g_flex_v legend-container'>
                        <div className='legend-item'>
                            <div className='legend-indicator insufficient'></div>
                            <span>Insufficient balance</span>
                        </div>
                        <div className='legend-item'>
                            <div className='legend-indicator dsra-dip'></div>
                            <span>DSRA dip</span>
                        </div>
                        <div className='legend-item'>
                            <div className='legend-indicator sufficient'></div>
                            <span>Sufficient balance</span>
                        </div>
                        <div className='legend-item'>
                            <div className='legend-indicator closed'></div>
                            <span>Closed loan</span>
                        </div>
                    </div>
                </div>
            </CardUI>
            {showCollectionConfirm && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setShowCollectionConfirm(false)}
                    titleLabel="Confirm Collection EMI"
                >
                    <div style={{ padding: '0px 10px 10px 10px' }}>
                        <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#333' }}>
                            Are you sure you want to initiate collection EMI process?
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowCollectionConfirm(false)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    background: '#fff',
                                    color: '#333',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowCollectionConfirm(false)
                                    onCollectionEMI()
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #28a745',
                                    background: '#28a745',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Proceed
                            </button>
                        </div>
                    </div>
                </ModalUI>
            )}
            {showApprovePayablesModal && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setShowApprovePayablesModal(false)}
                    titleLabel="Save EMI Tracker & Approve Payables"
                >
                    <div style={{ padding: '0px 10px 10px 10px' }}>
                        <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#333' }}>
                            Are you sure you want to save EMI Tracker and approve all pending payables?
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowApprovePayablesModal(false)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    background: '#fff',
                                    color: '#333',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowApprovePayablesModal(false)
                                    onSaveAndApprove()
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #007bff',
                                    background: '#007bff',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Approve
                            </button>
                        </div>
                    </div>
                </ModalUI>
            )}
        </div>
    );
};

const MonthOverMonthSummaryCard = ({ totals, isRetail, fmt, pick, renderThreeCol }) => {
    return (
        <CardUI title='Month-over-Month Summary' style={{ minWidth: 420, flex: '1 1 520px' }} className='g_m_bottom_100'>
            <table style={{ borderCollapse: 'collapse' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th style={{ padding: '8px' }}></th>
                        <th className='g_text_left' style={{ padding: '8px' }}>{totals.monthName || 'Current'}</th>
                        <th className='g_text_left' style={{ padding: '8px' }}>Previous</th>
                        <th className='g_text_left' style={{ padding: '8px' }}>Difference</th>
                    </tr>
                </thead>
                <tbody>
                    {renderThreeCol('Total to collect', pick(totals.grandToCollectTotalF4, totals.grandToCollectTotalRF4), pick(totals.grandPreviousToCollectTotalF4, totals.grandPreviousToCollectTotalRF4), pick(totals.grandToCollectDifferenceTotalF4, totals.grandToCollectDifferenceTotalRF4), 'g_success')}
                    {renderThreeCol('Overdues', pick(totals.overdueAccountTotalF4, totals.overdueAccountTotalRF4), pick(totals.previousOverdueTotalF4, totals.previousOverdueTotalRF4), pick(totals.differenceOverdueTotalF4, totals.differenceOverdueTotalRF4), 'g_warning')}
                    {renderThreeCol('Current Dues', pick(totals.toCollectToDefaultDifferenceCurrentF4, totals.toCollectToDefaultDifferenceCurrentRF4), pick(totals.toCollectToDefaultDifferencePreviousF4, totals.toCollectToDefaultDifferencePreviousRF4), pick(totals.differenceCurrentDemandF4, totals.differenceCurrentDemandRF4), '')}
                </tbody>
            </table>
        </CardUI>
    )
}

const CollectionsOverviewCard = ({ totals, isRetail, fmt, pick }) => {
    return (
        <CardUI title='Collections Overview' style={{ minWidth: 420, flex: '1 1 520px' }} className='g_m_bottom_100'>
            <table style={{ borderCollapse: 'collapse' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='g_text_left' style={{ padding: '8px' }}>Particulars</th>
                        <th className='g_text_left' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className='left' style={{ padding: '8px' }}><b>Total to collect</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.grandToCollectTotalF4, totals.grandToCollectTotalRF4))}</b></td>
                    </tr>
                    <tr>
                        <td className='left' style={{ padding: '8px' }}>Overdues</td>
                        <td className='g_text_right' style={{ padding: '8px' }}>{fmt(pick(totals.overdueAccountTotalF4, totals.overdueAccountTotalRF4))}</td>
                    </tr>
                    <tr>
                        <td className='left' style={{ padding: '8px' }}>Expected Non-payments</td>
                        <td className='g_text_right' style={{ padding: '8px' }}>{fmt(pick(totals.expectedFurtherDefaultAccountTotalF4, totals.expectedFurtherDefaultAccountTotalRF4))}</td>
                    </tr>
                    <tr>
                        <td className='left' style={{ padding: '8px' }}><b>Proposed Collections</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.grandTotalExpectedCollectionF4, totals.grandTotalExpectedCollectionRF4))}</b></td>
                    </tr>
                </tbody>
            </table>
        </CardUI>
    )
}

const DueVsExpectedCollectionsCard = ({ dueVsExpectedRows, formatAmount, hideTitle }) => {
    const recordCount = dueVsExpectedRows ? dueVsExpectedRows.length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='g_text_left' style={{ padding: '8px' }}>Particulars</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Due</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Proposed Collections</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Difference</th>
                    </tr>
                </thead>
                <tbody>
                    {(dueVsExpectedRows || []).map(({ label, due, expected, difference, isTotal }, idx) => (
                        <tr key={idx} className={isTotal ? 'is_total foot' : ''}>
                            <td className='left' style={{ padding: '8px' }}>{isTotal ? <b>{label}</b> : label}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{isTotal ? <b>{formatAmount(due)}</b> : formatAmount(due)}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{isTotal ? <b>{formatAmount(expected)}</b> : formatAmount(expected)}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{isTotal ? <b>{formatAmount(difference)}</b> : formatAmount(difference)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </PopupTableContainer>
    )
}

const NACHExpectedCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    const mapData = pick(totals.nachExpectedClientsTotalMap, totals.nachExpectedClientsTotalRMap)
    const recordCount = mapData ? Object.keys(mapData).length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{k}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapData[k])}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.nachExpectedClientsTotalF4, totals.nachExpectedClientsTotalRF4))}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const InflowExpectedCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    const mapData = pick(totals.inflowExpectedClientsTotalMap, totals.inflowExpectedClientsTotalRMap)
    const recordCount = mapData ? Object.keys(mapData).length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{k}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapData[k])}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.inflowExpectedClientsTotalF4, totals.inflowExpectedClientsTotalRF4))}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const DSRAExpectedCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    const mapData = pick(totals.dsraExpectedClientsTotalMap, totals.dsraExpectedClientsTotalRMap)
    const recordCount = mapData ? Object.keys(mapData).length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{k}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapData[k])}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.dsraExpectedClientsTotalF4, totals.dsraExpectedClientsTotalRF4) || pick(totals.grandTotalCollectionsFromDSRABalanceTotalF4, totals.grandTotalCollectionsFromDSRABalanceTotalRF4))}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const SurplusExpectedCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    const mapData = pick(totals.surplusExpectedClientsTotalMap, totals.surplusExpectedClientsTotalRMap)
    const recordCount = mapData ? Object.keys(mapData).length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{k}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapData[k])}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.surplusExpectedClientsTotalF4, totals.surplusExpectedClientsTotalRF4) || pick(totals.grandTotalCollectionsFromSurplusBalanceTotalF4, totals.grandTotalCollectionsFromSurplusBalanceTotalRF4))}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const OverduesAndExpectedDefaultsCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    const mapData = pick(totals.overdueAndExpectedDefaultsAccountTotalMap, totals.overdueAndExpectedDefaultsAccountTotalRMap)
    const recordCount = mapData ? Object.keys(mapData).length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{k}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapData[k])}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.overdueAndExpectedDefaultsAccountTotalF4, totals.overdueAndExpectedDefaultsAccountTotalRF4))}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const ProposedCollectionVsPaymentInitiated = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    const mapData = pick(totals.proposedVsActualCollectionMap, totals.proposedVsActualCollectionRMap)
    const recordCount = mapData ? Object.keys(mapData).length : 0
    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{k}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapData[k])}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{fmt(pick(totals.proposedVsActualCollectionTotalF4, totals.proposedVsActualCollectionTotalRF4))}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const ExpectedFurtherDefaultsCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    // Use expectedFurtherDefaultAccountTotalMap if isRetail is false, expectedFurtherDefaultAccountTotalRMap if true
    const mapData = isRetail
        ? (totals.expectedFurtherDefaultAccountTotalRMap || {})
        : (totals.expectedFurtherDefaultAccountTotalMap || {})

    // Helper function to parse amount
    const parseAmount = (value) => {
        if (value === null || value === undefined || value === '') return 0
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string') {
            const cleaned = value.replace(/,/g, '').replace(/[^\d.-]/g, '')
            if (!cleaned) return 0
            const parsed = parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
    }

    // Format amount with commas and 2 decimal places
    const formatAmount = (value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return '-'
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) return '-'
        return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    // Get account list from totals
    const accountList = Array.isArray(totals && totals.accountEMITrackerList) ? totals.accountEMITrackerList : []

    // Create maps for principal, interest, and other charges for each account
    const accountPrincipalMap = {}
    const accountInterestMap = {}
    const accountOtherChargesMap = {}
    const accountTotalMap = {}
    const accountCollectionsFromESCCMap = {}

    if (accountList && mapData) {
        const accountNamesInMap = Object.keys(mapData)
        accountList.forEach(account => {
            if (account && account.accountName && accountNamesInMap.includes(account.accountName)) {
                // Check account type individually (matching table logic)
                const isAccountRetail = account.accountType === 'individual'

                // Get values for each account - use RF4 for retail accounts, F4 for business accounts
                const principal = isAccountRetail
                    ? account.principalTotalRF4
                    : account.principalTotalF4
                const interest = isAccountRetail
                    ? account.interestTotalRF4
                    : account.interestTotalF4
                const intOnOverdueIntCurrent = isAccountRetail
                    ? account.intOnOverdueIntTotalCurrentRF4
                    : account.intOnOverdueIntTotalCurrentF4
                const intOnOverduePrincipalCurrent = isAccountRetail
                    ? account.intOnOverduePrincipalTotalCurrentRF4
                    : account.intOnOverduePrincipalTotalCurrentF4
                const intOnOverdueOOPECurrent = isAccountRetail
                    ? account.intOnOverdueOOPETotalCurrentRF4
                    : account.intOnOverdueOOPETotalCurrentF4
                const oope = isAccountRetail
                    ? account.oopeTotalRF4
                    : account.oopeTotalF4
                const defaultInt = isAccountRetail
                    ? account.defaultIntTotalRF4
                    : account.defaultIntTotalF4
                const tds = isAccountRetail
                    ? account.tdsTotalRF4
                    : account.tdsTotalF4
                const gst = isAccountRetail
                    ? account.gstTotalRF4
                    : account.gstTotalF4
                const esccBalance = isAccountRetail
                    ? account.esccBalanceRF4
                    : account.esccBalanceF4

                // Calculate total: Interest on overdue (Interest, Principal, OOPE) current + OOPE + default Interest - TDS + GST
                const otherCharges = parseAmount(intOnOverdueIntCurrent) +
                    parseAmount(intOnOverduePrincipalCurrent) +
                    parseAmount(intOnOverdueOOPECurrent) +
                    parseAmount(oope) +
                    parseAmount(defaultInt) -
                    parseAmount(tds) +
                    parseAmount(gst)
                // Split into Principal, Interest, and Other Charges
                // Principal: Use account.principalTotalF4/RF4
                const principalAmount = parseAmount(principal)

                // Interest: Use account.interestTotalF4/RF4
                const interestAmount = parseAmount(interest)

                const total = principalAmount + interestAmount + otherCharges - parseAmount(esccBalance)

                let collectionsFromESCC = 0;

                if(total >= 0){
                    collectionsFromESCC = parseAmount(esccBalance)
                }else{
                    collectionsFromESCC = parseAmount(esccBalance) + total
                }

                accountPrincipalMap[account.accountName] = principalAmount
                accountInterestMap[account.accountName] = interestAmount
                accountOtherChargesMap[account.accountName] = otherCharges
                accountTotalMap[account.accountName] = total > 0 ? total : 0
                accountCollectionsFromESCCMap[account.accountName] = collectionsFromESCC
            }
        })
    }

    // Calculate totals
    let totalPrincipal = 0
    let totalInterest = 0
    let totalOtherCharges = 0
    let totalAmount = 0
    let totalCollectionsFromESCC = 0
    if (mapData) {
        Object.keys(mapData).forEach(accountName => {
            const principal = accountPrincipalMap[accountName] || 0
            const interest = accountInterestMap[accountName] || 0
            const otherCharges = accountOtherChargesMap[accountName] || 0
            const total = accountTotalMap[accountName] || 0
            const collectionsFromESCC = accountCollectionsFromESCCMap[accountName] || 0

            totalAmount += total
            totalPrincipal += principal
            totalInterest += interest
            totalOtherCharges += otherCharges
            totalCollectionsFromESCC += collectionsFromESCC
        })
    }

    const recordCount = mapData ? Object.keys(mapData).length : 0

    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Principal</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Interest</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Other Charges</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Collection From ESCC</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Total Expected Default</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => {
                        const principal = accountPrincipalMap[k] || 0
                        const interest = accountInterestMap[k] || 0
                        const otherCharges = accountOtherChargesMap[k] || 0
                        const total = accountTotalMap[k] || 0
                        const collectionsFromESCC = accountCollectionsFromESCCMap[k] || 0
                        return (
                            <tr key={idx}>
                                <td className='left' style={{ padding: '8px' }}>{k}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(principal)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(interest)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(otherCharges)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(collectionsFromESCC)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(total)}</td>
                            </tr>
                        )
                    })}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalPrincipal)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalInterest)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalOtherCharges)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalCollectionsFromESCC)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalAmount)}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const CurrentDemandBreakupCard = ({ totals, isRetail, fmt, pick }) => {
    // Get values from totals using the pick function to handle business/retail
    const getValue = (baseKey, retailKey) => {
        const value = pick(totals[baseKey], totals[retailKey])
        return value !== null && value !== undefined ? value : '0'
    }

    // Try multiple possible field names for each value
    const getEMI = () => {
        const keys = isRetail
            ? ['grandEmiTotalRF4', 'grandEMITotalRF4', 'totalEmiDueRF4']
            : ['grandEmiTotalF4', 'grandEMITotalF4', 'totalEmiDueF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getPrincipal = () => {
        const keys = isRetail
            ? ['grandPrincipalTotalRF4', 'totalPrincipalDueRF4', 'grandPrincipalAmountRF4']
            : ['grandPrincipalTotalF4', 'totalPrincipalDueF4', 'grandPrincipalAmountF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getInterest = () => {
        const keys = isRetail
            ? ['grandInterestTotalRF4', 'totalInterestDueRF4', 'grandInterestAmountRF4']
            : ['grandInterestTotalF4', 'totalInterestDueF4', 'grandInterestAmountF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getIntOnOverdueInterest = () => {
        // Prefer new *CurrentTotal* fields, fall back to older names if needed
        const keys = isRetail
            ? ['grandIntOnOverdueIntCurrentTotalRF4', 'grandIntOnOverdueIntTotalRF4', 'totalIntOnOverdueIntDueCurrentRF4']
            : ['grandIntOnOverdueIntCurrentTotalF4', 'grandIntOnOverdueIntTotalF4', 'totalIntOnOverdueIntDueCurrentF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getIntOnOverduePrincipal = () => {
        // Prefer new *CurrentTotal* fields, fall back to older names if needed
        const keys = isRetail
            ? ['grandIntOnOverduePrincipalCurrentTotalRF4', 'grandIntOnOverduePrincipalTotalRF4', 'totalIntOnOverduePrincipalDueRF4']
            : ['grandIntOnOverduePrincipalCurrentTotalF4', 'grandIntOnOverduePrincipalTotalF4', 'totalIntOnOverduePrincipalDueCurrentF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getOOPE = () => {
        const keys = isRetail
            ? ['grandOOPETotalRF4', 'totalOOPEDueRF4', 'oopeTotalRF4']
            : ['grandOOPETotalF4', 'totalOOPEDueF4', 'oopeTotalF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getIntOnOverdueOOPE = () => {
        // Prefer new *CurrentTotal* fields, fall back to older names if needed
        const keys = isRetail
            ? ['grandIntOnOverdueOOPECurrentTotalRF4', 'grandIntOnOverdueOOPETotalRF4', 'totalIntOnOverdueOOPEDueCurrentRF4']
            : ['grandIntOnOverdueOOPECurrentTotalF4', 'grandIntOnOverdueOOPETotalF4', 'totalIntOnOverdueOOPEDueCurrentF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getGST = () => {
        const keys = isRetail
            ? ['grandGstTotalRF4', 'grandGSTTotalRF4', 'totalGstDueRF4']
            : ['grandGstTotalF4', 'grandGSTTotalF4', 'totalGstDueF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getTDS = () => {
        const keys = isRetail
            ? ['grandTDSTotalRF4', 'totalTDSDueRF4']
            : ['grandTDSTotalF4', 'totalTDSDueF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const getPenalCharge = () => {
        const keys = isRetail
            ? ['grandDefaultIntTotalRF4', 'totalPenalChargesDueRF4', 'grandPenalChargesTotalRF4']
            : ['grandDefaultIntTotalF4', 'totalPenalChargesDueF4', 'grandPenalChargesTotalF4']
        for (const key of keys) {
            if (totals[key]) return totals[key]
        }
        return '0.00'
    }

    const breakdownData = [
        { particular: 'Principal', amount: getPrincipal() },
        { particular: 'Interest', amount: getInterest() },
        // { particular: 'EMI', amount: getEMI() },
        { particular: 'Int On overdue Interest', amount: getIntOnOverdueInterest() },
        { particular: 'Int on overdue Principal', amount: getIntOnOverduePrincipal() },
        { particular: 'OOPE', amount: getOOPE() },
        { particular: 'Int on overdue OOPE', amount: getIntOnOverdueOOPE() },
        { particular: 'Penal Charge', amount: getPenalCharge() },
        { particular: 'GST', amount: getGST() },
        { particular: 'TDS', amount: getTDS() }
    ]
    const recordCount = breakdownData.length

    // Calculate total by summing all amounts
    const parseAmount = (value) => {
        if (value === null || value === undefined || value === '') return 0
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string') {
            const cleaned = value.replace(/,/g, '').replace(/[^\d.-]/g, '')
            if (!cleaned) return 0
            const parsed = parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
    }

    const totalAmount = breakdownData.reduce((sum, row) => {
        const amount = parseAmount(row.amount)
        // Subtract TDS, add all others
        if (row.particular === 'TDS') {
            return sum - amount
        }
        return sum + amount
    }, 0)

    // Format total amount with commas
    const formatTotalAmount = (amount) => {
        if (amount === null || amount === undefined || Number.isNaN(amount)) return '-'
        const numeric = Number(amount)
        if (!Number.isFinite(numeric)) return '-'
        return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Particulars (current)</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {breakdownData.map((row, idx) => (
                        <tr key={idx}>
                            <td className='left' style={{ padding: '8px' }}>{row.particular}</td>
                            <td className='g_text_right' style={{ padding: '8px' }}>{fmt(row.amount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatTotalAmount(totalAmount)}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const OverdueBreakUpCard = ({ totals, isRetail, fmt, pick, renderMapRows, hideTitle }) => {
    // Use overdueAccountTotalMap if isRetail is false, overdueAccountTotalRMap if true
    const mapData = isRetail
        ? (totals.overdueAccountTotalRMap || {})
        : (totals.overdueAccountTotalMap || {})

    // Helper function to parse amount
    const parseAmount = (value) => {
        if (value === null || value === undefined || value === '') return 0
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string') {
            const cleaned = value.replace(/,/g, '').replace(/[^\d.-]/g, '')
            if (!cleaned) return 0
            const parsed = parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
    }

    // Format amount with commas and 2 decimal places
    const formatAmount = (value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return '-'
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) return '-'
        return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    // Get account list from totals
    const accountList = Array.isArray(totals && totals.accountEMITrackerList) ? totals.accountEMITrackerList : []

    // Create maps for overduePrincipal and overdueInterest from accountList
    // Only include accounts that are in the overdueAccountTotalMap
    const overduePrincipalMap = {}
    const overdueInterestMap = {}

    if (accountList && mapData) {
        const accountNamesInMap = Object.keys(mapData)
        accountList.forEach(account => {
            if (account && account.accountName && accountNamesInMap.includes(account.accountName)) {
                // Get overdue principal and interest for these accounts
                // Use retail fields if isRetail is true, business fields if false
                const principalValue = isRetail
                    ? account.overduePrincipalTotalRF4
                    : account.overduePrincipalTotalF4
                const interestValue = isRetail
                    ? account.overdueInterestTotalRF4
                    : account.overdueInterestTotalF4

                if (principalValue) overduePrincipalMap[account.accountName] = principalValue
                if (interestValue) overdueInterestMap[account.accountName] = interestValue
            }
        })
    }

    // Calculate totals
    let totalPrincipal = 0
    let totalInterest = 0
    let totalOtherCharges = 0
    let totalAmount = 0

    if (mapData) {
        Object.keys(mapData).forEach(accountName => {
            const principal = parseAmount(overduePrincipalMap[accountName] || 0)
            const interest = parseAmount(overdueInterestMap[accountName] || 0)
            const otherCharges = (parseAmount(mapData[accountName]) - principal - interest) > 0 ? (parseAmount(mapData[accountName]) - principal - interest) : 0

            totalPrincipal += principal
            totalInterest += interest
            totalOtherCharges += otherCharges
            totalAmount += principal + interest + otherCharges
        })
    }

    const recordCount = mapData ? Object.keys(mapData).length : 0

    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Principal Overdue</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Interest Overdue</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Others</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => {
                        const amount = parseAmount(mapData[k] || 0)
                        const principal = parseAmount(overduePrincipalMap[k] || 0)
                        const interest = parseAmount(overdueInterestMap[k] || 0)
                        const otherCharges = (amount - principal - interest) > 0 ? (amount - principal - interest) : 0

                        return (
                            <tr key={idx}>
                                <td className='left' style={{ padding: '8px' }}>{k}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(principal)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(interest)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(otherCharges)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(amount)}</td>
                            </tr>
                        )
                    })}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalPrincipal)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalInterest)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalOtherCharges)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalAmount)}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const ExpectedCollectionFromOverdueCard = ({ totals, isRetail }) => {
    const mapData = isRetail
        ? (totals.expectedCollectionFromOverdueAccountTotalRMap || {})
        : (totals.expectedCollectionFromOverdueAccountTotalMap || {})

    const parseAmount = (value) => {
        if (value === null || value === undefined || value === '') return 0
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string') {
            const cleaned = value.replace(/,/g, '').replace(/[^\d.-]/g, '')
            if (!cleaned) return 0
            const parsed = parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
    }

    const formatAmount = (value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return '-'
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) return '-'
        return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const accountList = Array.isArray(totals && totals.accountEMITrackerList) ? totals.accountEMITrackerList : []
    const overduePrincipalMap = {}
    const overdueInterestMap = {}

    if (accountList && mapData) {
        const accountNamesInMap = Object.keys(mapData)
        accountList.forEach(account => {
            if (account && account.accountName && accountNamesInMap.includes(account.accountName)) {
                const principalValue = isRetail
                    ? account.overduePrincipalTotalRF4
                    : account.overduePrincipalTotalF4
                const interestValue = isRetail
                    ? account.overdueInterestTotalRF4
                    : account.overdueInterestTotalF4
                if (principalValue) overduePrincipalMap[account.accountName] = principalValue
                if (interestValue) overdueInterestMap[account.accountName] = interestValue
            }
        })
    }

    let totalPrincipal = 0
    let totalInterest = 0
    let totalOtherCharges = 0
    let totalAmount = 0

    if (mapData) {
        Object.keys(mapData).forEach(accountName => {
            let amount = parseAmount(mapData[accountName] || 0)
            const principal = parseAmount(overduePrincipalMap[accountName] || 0)
            const interest = parseAmount(overdueInterestMap[accountName] || 0)
            const otherCharges = (parseAmount(mapData[accountName]) - principal - interest) > 0 ? (parseAmount(mapData[accountName]) - principal - interest) : 0

            let grandOtherCharges = 0
            let grandPrincipal = 0
            let grandInterest = 0
            if (amount >= otherCharges) {
                grandOtherCharges = otherCharges
                amount -= otherCharges
            } else {
                grandOtherCharges = amount
                amount = 0
            }
            if (amount >= interest) {
                grandInterest = interest
                amount -= interest
            } else {
                grandInterest = amount
                amount = 0
            }
            if (amount >= principal) {
                grandPrincipal = principal
                amount -= principal
            } else {
                grandPrincipal = amount
                amount = 0
            }

            totalPrincipal += grandPrincipal
            totalInterest += grandInterest
            totalOtherCharges += grandOtherCharges
            totalAmount += grandPrincipal + grandInterest + grandOtherCharges
        })
    }

    const recordCount = mapData ? Object.keys(mapData).length : 0

    return (
        <PopupTableContainer recordCount={recordCount}>
            <table style={{ width: '100%' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th className='left' style={{ padding: '8px' }}>Account</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Principal Overdue</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Interest Overdue</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Others</th>
                        <th className='g_text_right' style={{ padding: '8px' }}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {mapData && Object.keys(mapData).map((k, idx) => {
                        const finalAmount = parseAmount(mapData[k] || 0)
                        const principal = parseAmount(overduePrincipalMap[k] || 0)
                        const interest = parseAmount(overdueInterestMap[k] || 0)
                        const otherCharges = (finalAmount - principal - interest) > 0 ? (finalAmount - principal - interest) : 0

                        let amountToDistribute = finalAmount
                        let grandOtherCharges = 0
                        let grandPrincipal = 0
                        let grandInterest = 0
                        if (amountToDistribute >= otherCharges) {
                            grandOtherCharges = otherCharges
                            amountToDistribute -= otherCharges
                        } else {
                            grandOtherCharges = finalAmount
                            amountToDistribute = 0
                        }
                        if (amountToDistribute >= interest) {
                            grandInterest = interest
                            amountToDistribute -= interest
                        } else {
                            grandInterest = amountToDistribute
                            amountToDistribute = 0
                        }
                        if (amountToDistribute >= principal) {
                            grandPrincipal = principal
                            amountToDistribute -= principal
                        } else {
                            grandPrincipal = amountToDistribute
                            amountToDistribute = 0
                        }

                        return (
                            <tr key={idx}>
                                <td className='left' style={{ padding: '8px' }}>{k}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(grandPrincipal)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(grandInterest)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(grandOtherCharges)}</td>
                                <td className='g_text_right' style={{ padding: '8px' }}>{formatAmount(finalAmount)}</td>
                            </tr>
                        )
                    })}
                </tbody>
                <tfoot>
                    <tr className='foot'>
                        <td className='left' style={{ padding: '8px' }}><b>Total</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalPrincipal)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalInterest)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalOtherCharges)}</b></td>
                        <td className='g_text_right' style={{ padding: '8px' }}><b>{formatAmount(totalAmount)}</b></td>
                    </tr>
                </tfoot>
            </table>
        </PopupTableContainer>
    )
}

const SummaryTableCard = ({ totals, isRetail, fmt, pick, formatAmount, parseAmountToNumber, renderMapRows, dueVsExpectedRows, auditLogsResponse }) => {
    const [openPopup, setOpenPopup] = useState(null)
    const [showCollectionDetails, setShowCollectionDetails] = useState(false)
    const isEmiTrackerSaved = !!(auditLogsResponse && auditLogsResponse.emiTrackerSavedDateISO)

    const getCurrentMonthValue = (baseKey, retailKey) => {
        const value = pick(totals[baseKey], totals[retailKey])
        return parseAmountToNumber(value)
    }

    const getPreviousMonthValue = (baseKey, retailKey, previousKeyOverride) => {
        // Use override if provided, otherwise try to construct previous key
        if (previousKeyOverride) {
            const prevBaseKey = previousKeyOverride.baseKey
            const prevRetailKey = previousKeyOverride.retailKey
            const value = pick(totals[prevBaseKey], totals[prevRetailKey])
            return parseAmountToNumber(value)
        }
        // Try common patterns
        const prevBaseKey = baseKey.replace('TotalF4', 'PreviousTotalF4').replace('F4', 'PreviousF4')
        const prevRetailKey = retailKey ? retailKey.replace('TotalRF4', 'PreviousTotalRF4').replace('RF4', 'PreviousRF4') : null
        const value = pick(totals[prevBaseKey], totals[prevRetailKey])
        return parseAmountToNumber(value)
    }

    const calculateDifference = (current, previous) => {
        return (current == null ? 0 : current) - (previous == null ? 0 : previous)
    }

    const getMonthName = () => {
        if (totals && totals.monthName) {
            // Convert abbreviated month to full name if needed
            const monthMap = {
                'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 'Apr': 'April',
                'May': 'May', 'Jun': 'June', 'Jul': 'July', 'Aug': 'August',
                'Sep': 'September', 'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
            }
            return monthMap[totals.monthName] || totals.monthName
        }
        if (totals && totals.yearMonth && totals.yearMonth.monthName) {
            const monthMap = {
                'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 'Apr': 'April',
                'May': 'May', 'Jun': 'June', 'Jul': 'July', 'Aug': 'August',
                'Sep': 'September', 'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
            }
            return monthMap[totals.yearMonth.monthName] || totals.yearMonth.monthName
        }
        if (totals && totals.yearMonth && totals.yearMonth.month) {
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
            return monthNames[totals.yearMonth.month - 1]
        }
        return 'Current'
    }

    const getPreviousMonthName = () => {
        if (totals && totals.previousMonthName) {
            const monthMap = {
                'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 'Apr': 'April',
                'May': 'May', 'Jun': 'June', 'Jul': 'July', 'Aug': 'August',
                'Sep': 'September', 'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
            }
            return monthMap[totals.previousMonthName] || totals.previousMonthName
        }
        if (totals && totals.yearMonth) {
            const month = totals.yearMonth.month
            const year = totals.yearMonth.year
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
            if (month === 1) {
                return monthNames[11] // December
            } else {
                return monthNames[month - 2]
            }
        }
        return 'Previous'
    }

    const currentMonthName = getMonthName()
    const previousMonthName = getPreviousMonthName()

    // Row A: Total to Collect
    const totalToCollectCurrent = getCurrentMonthValue('grandToCollectTotalF4', 'grandToCollectTotalRF4')
    const totalToCollectPrevious = getPreviousMonthValue('grandToCollectTotalF4', 'grandToCollectTotalRF4', { baseKey: 'grandPreviousToCollectTotalF4', retailKey: 'grandPreviousToCollectTotalRF4' })
    const totalToCollectDiff = calculateDifference(totalToCollectCurrent, totalToCollectPrevious)

    // Row B: Overdues
    const overduesCurrent = getCurrentMonthValue('overdueAccountTotalF4', 'overdueAccountTotalRF4')
    const overduesPrevious = getPreviousMonthValue('overdueAccountTotalF4', 'overdueAccountTotalRF4', { baseKey: 'previousOverdueTotalF4', retailKey: 'previousOverdueTotalRF4' })
    const overduesDiff = calculateDifference(overduesCurrent, overduesPrevious)

    // Row C: Current Demand
    const currentDemandCurrent = getCurrentMonthValue('toCollectToDefaultDifferenceCurrentF4', 'toCollectToDefaultDifferenceCurrentRF4')
    // Previous month = Previous Total to Collect - Previous Overdues
    const currentDemandPrevious = (totalToCollectPrevious !== null && overduesPrevious !== null)
        ? (totalToCollectPrevious - overduesPrevious)
        : null
    const currentDemandDiff = calculateDifference(currentDemandCurrent, currentDemandPrevious)

    // Row D: Expected Further Default
    const expectedFurtherDefaultCurrent = getCurrentMonthValue('expectedFurtherDefaultAccountTotalF4', 'expectedFurtherDefaultAccountTotalRF4')
    const expectedFurtherDefaultPrevious = getPreviousMonthValue('expectedFurtherDefaultAccountTotalF4', 'expectedFurtherDefaultAccountTotalRF4', { baseKey: 'expectedDefaultsPreviousTotalF4', retailKey: 'expectedDefaultsPreviousTotalRF4' })
    const expectedFurtherDefaultDiff = calculateDifference(expectedFurtherDefaultCurrent, expectedFurtherDefaultPrevious)

    // Row D: Expected Further Default
    const expectedCollectionFromOverdue = getCurrentMonthValue('expectedCollectionFromOverdueF4', 'expectedCollectionFromOverdueRF4')
    const expectedCollectionFromOverduePrevious = getPreviousMonthValue('previousExpectedCollectionFromOverdueF4', 'retialPreviousExpectedCollectionFromOverdueF4', { baseKey: 'previousExpectedCollectionFromOverdueF4', retailKey: 'retialPreviousExpectedCollectionFromOverdueF4' })
    const expectedCollectionFromOverdueDiff = calculateDifference(expectedCollectionFromOverdue, expectedCollectionFromOverduePrevious)

    // Row E: Overdues + Expected Default
    const overduesPlusExpectedCurrent = (overduesCurrent || 0) + (expectedFurtherDefaultCurrent || 0)
    const overduesPlusExpectedPrevious = (overduesPrevious || 0) + (expectedFurtherDefaultPrevious || 0)
    const overduesPlusExpectedDiff = calculateDifference(overduesPlusExpectedCurrent, overduesPlusExpectedPrevious)

    // Row F: Expected Collection
    // const expectedCollectionCurrent = getCurrentMonthValue('grandTotalExpectedCollectionF4', 'grandTotalExpectedCollectionRF4')
    const expectedCollectionCurrent = (currentDemandCurrent || 0) - (expectedFurtherDefaultCurrent || 0) + (expectedCollectionFromOverdue || 0)

    // Previous month = Previous Current Demand - Previous Expected Further Default
    const expectedCollectionPrevious = (currentDemandPrevious || 0) - (expectedFurtherDefaultPrevious || 0) + (expectedCollectionFromOverduePrevious || 0)
    const expectedCollectionDiff = calculateDifference(expectedCollectionCurrent, expectedCollectionPrevious)

    // Row G: Collection Through NACH
    const nachCurrent = getCurrentMonthValue('nachExpectedClientsTotalF4', 'nachExpectedClientsTotalRF4')
    const nachPrevious = getPreviousMonthValue('nachExpectedClientsTotalF4', 'nachExpectedClientsTotalRF4', { baseKey: 'nachExpectedPreviousF4', retailKey: 'nachExpectedPreviousRF4' })
    const nachDiff = calculateDifference(nachCurrent, nachPrevious)

    // Row H: Inflow expected
    const inflowExpectedCurrent = getCurrentMonthValue('inflowExpectedClientsTotalF4', 'inflowExpectedClientsTotalRF4')
    const inflowExpectedPrevious = getPreviousMonthValue('inflowExpectedClientsTotalF4', 'inflowExpectedClientsTotalRF4', { baseKey: 'inflowExpectedPreviousF4', retailKey: 'inflowExpectedPreviousRF4' })
    const inflowExpectedDiff = calculateDifference(inflowExpectedCurrent, inflowExpectedPrevious)

    //
    const paymentInitiatedCurrent = getCurrentMonthValue('grandPaymentInitiatedF4', 'grandPaymentInitiatedRF4')
    const paymentInitiatedPrevious = getPreviousMonthValue('grandPaymentInitiatedF4', 'grandPaymentInitiatedRF4', { baseKey: 'grandPreviousMonthPaymentInitiatedTotalF4', retailKey: 'grandPreviousMonthPaymentInitiatedTotalRF4' })
    const paymentInitiatedDiff = calculateDifference(paymentInitiatedCurrent, paymentInitiatedPrevious)

    const proposedVsExpectedCollectionDiffCurrent = (expectedCollectionCurrent || 0) - (paymentInitiatedCurrent || 0)
    const proposedVsExpectedCollectionDiffPrevious = (expectedCollectionPrevious || 0) - (paymentInitiatedPrevious || 0)
    const proposedVsExpectedCollectionDiff = calculateDifference(proposedVsExpectedCollectionDiffCurrent, proposedVsExpectedCollectionDiffPrevious)

    // Row I: Collection Through Current ESCC Balance (F - G - H)
    // Calculate as Expected Collection - Collection through NACH - Inflow expected
    const esccCurrent = (expectedCollectionCurrent || 0) - (nachCurrent || 0) - (inflowExpectedCurrent || 0)
    const esccPrevious = (expectedCollectionPrevious || 0) - (nachPrevious || 0) - (inflowExpectedPrevious || 0)
    // Difference = Current month value - Previous month value
    const esccDiff = calculateDifference(esccCurrent, esccPrevious)
    const dsraCollectionCurrent = getCurrentMonthValue('grandTotalCollectionsFromDSRABalanceTotalF4', 'grandTotalCollectionsFromDSRABalanceTotalRF4')
    const dsraCollectionPrevious = getPreviousMonthValue('grandTotalCollectionsFromDSRABalanceTotalF4', 'grandTotalCollectionsFromDSRABalanceTotalRF4', {
        baseKey: 'grandTotalPreviousCollectionsFromDSRABalanceTotalF4',
        retailKey: 'grandTotalPreviousCollectionsFromDSRABalanceTotalF4'
    })
    const dsraCollectionDiff = calculateDifference(dsraCollectionCurrent, dsraCollectionPrevious)
    const surplusCollectionCurrent = getCurrentMonthValue('grandTotalCollectionsFromSurplusBalanceTotalF4', 'grandTotalCollectionsFromSurplusBalanceTotalRF4')
    const surplusCollectionPrevious = getPreviousMonthValue('grandTotalCollectionsFromSurplusBalanceTotalF4', 'grandTotalCollectionsFromSurplusBalanceTotalRF4', {
        baseKey: 'grandTotalPreviousCollectionsFromSurplusBalanceTotalF4',
        retailKey: 'grandTotalPreviousCollectionsFromSurplusBalanceTotalF4'
    })
    const surplusCollectionDiff = calculateDifference(surplusCollectionCurrent, surplusCollectionPrevious)

    const scrollToGrandToCollectTotal = () => {
        const element = document.getElementById('grandToCollectTotal')
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            // Add highlight class with blue background
            element.classList.add('highlight-cell')
            setTimeout(() => {
                element.classList.remove('highlight-cell')
            }, 2000) // Keep highlighted for 2 seconds
        }
    }

    const scrollToGrandPaymentInitiated = () => {
        const element = document.getElementById('paymentInitiated')
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            // Add highlight class with blue background
            element.classList.add('highlight-cell')
            setTimeout(() => {
                element.classList.remove('highlight-cell')
            }, 2000) // Keep highlighted for 2 seconds
        }
    }

    const scrollToGrandExpectedCollectionTotal = () => {
        const element = document.getElementById('grandExpectedCollectionTotal')
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            element.classList.add('highlight-cell')
            setTimeout(() => {
                element.classList.remove('highlight-cell')
            }, 2000)
        }
    }

    const scrollToElement = (elementId) => {
        const element = document.getElementById(elementId)
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            element.classList.add('highlight-cell')
            setTimeout(() => {
                element.classList.remove('highlight-cell')
            }, 2000)
        }
    }

    const handleRowAClick = () => {
        scrollToGrandToCollectTotal()
    }

    const handleRowBClick = () => {
        setOpenPopup('overdueBreakUp')
    }

    const handleRowCClick = () => {
        scrollToGrandToCollectTotal()
    }

    const handleRowIClick = () => {
        scrollToGrandPaymentInitiated()
    }

    const handleRowDClick = () => {
        setOpenPopup('expectedFurtherDefaults')
    }

    const handleRowEClick = () => {
        setOpenPopup('expectedCollectionFromOverdue')
    }

    const handleRowJClick = () => {
        setOpenPopup('proposedCollectionVsPaymentInitiated')
    }

    const handleRowGClick = () => {
        setOpenPopup('nachExpected')
    }

    const handleRowFClick = () => {
        scrollToGrandExpectedCollectionTotal()
    }

    const handleDropdownClick = (e) => {
        e.stopPropagation() // Prevent triggering row click
        setShowCollectionDetails(!showCollectionDetails)
    }

    const handleRowHClick = () => {
        setOpenPopup('inflowExpected')
    }

    const handleDsraClick = () => {
        setOpenPopup('dsraExpected')
    }

    const handleSurplusClick = () => {
        setOpenPopup('surplusExpected')
    }

    const formatAmountWithZero = (value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return '-'
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) return '-'
        if (numeric === 0 || Math.abs(numeric) < 0.01) return '-'
        return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const renderRow = (sNo, particulars, current, previous, difference, onClickHandler, isClickable = false, showIcon = false, isExpanded = false, isHidden = false, onIconClick = null) => {
        const isMultiLine = particulars.includes('\n')
        if (isHidden) return null
        return (
            <tr key={sNo}>
                <td
                    style={{
                        padding: '8px',
                        textAlign: 'center',
                        position: 'relative',
                        cursor: showIcon && onIconClick ? 'pointer' : 'default'
                    }}
                    onClick={showIcon && onIconClick ? onIconClick : undefined}
                >
                    {showIcon && onIconClick && (
                        <span
                            className={`icon ${isExpanded ? 'icon-angle-down' : 'icon-angle-right'}`}
                            style={{
                                cursor: 'pointer',
                                position: 'absolute',
                                left: '4px',
                                fontWeight: 'bold',
                                fontSize: '14px'
                            }}
                            onClick={onIconClick}
                        />
                    )}
                    <b style={{ marginLeft: showIcon && onIconClick ? '5px' : '0' }}>{sNo}</b>
                </td>
                <td
                    className='left'
                    style={{
                        padding: '8px',
                        whiteSpace: isMultiLine ? 'pre-line' : 'normal',
                        cursor: showIcon && onIconClick ? 'pointer' : 'default'
                    }}
                    onClick={showIcon && onIconClick ? onIconClick : undefined}
                >
                    {particulars.split('\n').map((line, idx) => (
                        <span key={idx}>{line}{idx < particulars.split('\n').length - 1 ? <br /> : ''}</span>
                    ))}
                </td>
                <td
                    style={{
                        padding: '8px',
                        textAlign: 'center',
                        cursor: isClickable ? 'pointer' : 'default',
                        color: isClickable ? '#0066cc' : 'inherit',
                        textDecoration: isClickable ? 'underline' : 'none'
                    }}
                    onClick={isClickable && onClickHandler ? onClickHandler : undefined}
                    title={isClickable ? 'Click to view details' : ''}
                >
                    {formatAmount(current)}
                </td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{formatAmountWithZero(previous)}</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{formatAmountWithZero(difference)}</td>
            </tr>
        )
    }

    return (
        <CardUI title='Summary' className='g_m_bottom_100' style={{ minWidth: 600, flex: '1 1 100%' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }} className='table_cr table'>
                <thead>
                    <tr>
                        <th style={{ padding: '8px', textAlign: 'center', width: '5%' }}></th>
                        <th className='g_text_left' style={{ padding: '8px', width: '35%' }}>Particulars</th>
                        <th style={{ padding: '8px', textAlign: 'center', width: '20%' }}>{currentMonthName} (in INR)</th>
                        <th style={{ padding: '8px', textAlign: 'center', width: '20%' }}>{previousMonthName} (in INR)</th>
                        <th style={{ padding: '8px', textAlign: 'center', width: '20%' }}>Difference (INR)</th>
                    </tr>
                </thead>
                <tbody>
                    {/* {renderRow('A', 'Total to Collect (B + C)', totalToCollectCurrent, totalToCollectPrevious, totalToCollectDiff, handleRowAClick, true)} */}
                    {renderRow('A', 'Total to Collect', totalToCollectCurrent, totalToCollectPrevious, totalToCollectDiff, handleRowCClick, true)}
                    {renderRow('B', 'Overdues', overduesCurrent, overduesPrevious, overduesDiff, handleRowBClick, true)}
                    {renderRow('C', 'Current Dues (A - B)', currentDemandCurrent, currentDemandPrevious, currentDemandDiff, null, false)}
                    {renderRow('D', 'Expected Non-payments', expectedFurtherDefaultCurrent, expectedFurtherDefaultPrevious, expectedFurtherDefaultDiff, handleRowDClick, true)}
                    {renderRow('E', 'Expected Collection From Overdue', expectedCollectionFromOverdue, expectedCollectionFromOverduePrevious, expectedCollectionFromOverdueDiff, handleRowEClick, true)}
                    {renderRow('F', 'Total Proposed Collection (C - D + E)', expectedCollectionCurrent, expectedCollectionPrevious, expectedCollectionDiff, handleRowFClick, true, !isEmiTrackerSaved, showCollectionDetails, false, !isEmiTrackerSaved ? handleDropdownClick : null)}
                    {!isEmiTrackerSaved && showCollectionDetails && renderRow('F.1', 'Collection Through Current ESCC Balance (F - F.2 - F.3)', esccCurrent, esccPrevious, esccDiff, null, false)}
                    {!isEmiTrackerSaved && showCollectionDetails && renderRow('F.2', 'Inflow expected', inflowExpectedCurrent, inflowExpectedPrevious, inflowExpectedDiff, handleRowHClick, true)}
                    {!isEmiTrackerSaved && showCollectionDetails && renderRow('F.3', 'Collection from NACH', nachCurrent, nachPrevious, nachDiff, handleRowGClick, true)}
                    {!isEmiTrackerSaved && showCollectionDetails && renderRow('F.4', 'Collection from DSRA', dsraCollectionCurrent, dsraCollectionPrevious, dsraCollectionDiff, handleDsraClick, true)}
                    {!isEmiTrackerSaved && showCollectionDetails && renderRow('F.5', 'Collection from Surplus', surplusCollectionCurrent, surplusCollectionPrevious, surplusCollectionDiff, handleSurplusClick, true)}
                    {renderRow('G', 'Payment Initiated', paymentInitiatedCurrent, paymentInitiatedPrevious, paymentInitiatedDiff, handleRowIClick, true, isEmiTrackerSaved, showCollectionDetails, false, isEmiTrackerSaved ? handleDropdownClick : null)}
                    {isEmiTrackerSaved && showCollectionDetails && renderRow('G.1', 'Collection from DSRA', dsraCollectionCurrent, dsraCollectionPrevious, dsraCollectionDiff, handleDsraClick, true)}
                    {isEmiTrackerSaved && showCollectionDetails && renderRow('G.2', 'Collection from Surplus', surplusCollectionCurrent, surplusCollectionPrevious, surplusCollectionDiff, handleSurplusClick, true)}
                    {renderRow('H', 'Shortfall (F - G)', proposedVsExpectedCollectionDiffCurrent, proposedVsExpectedCollectionDiffPrevious, proposedVsExpectedCollectionDiff, handleRowJClick, true)}

                    {/* {renderRow('G', 'Overdues + Expected Further Default (B + D)', overduesPlusExpectedCurrent, overduesPlusExpectedPrevious, overduesPlusExpectedDiff, handleRowEClick, true)} */}
                </tbody>
            </table>
            {openPopup === 'overdueBreakUp' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Overdue BreakUp"
                >
                    <OverdueBreakUpCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {
                openPopup === 'proposedCollectionVsPaymentInitiated' && (
                    <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel='Proposed Collection vs Payment Initiated Difference'
                    >
                        <ProposedCollectionVsPaymentInitiated
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                        />
                    </ModalUI>
                )
            }
            {openPopup === 'expectedFurtherDefaults' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Expected Further Default"
                >
                    <ExpectedFurtherDefaultsCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'expectedCollectionFromOverdue' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Expected Collection From Overdues"
                >
                    <ExpectedCollectionFromOverdueCard
                        totals={totals}
                        isRetail={isRetail}
                    />
                </ModalUI>
            )}
            {openPopup === 'overduesAndExpectedDefaults' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Overdues and Expected Defaults (Uncollected)"
                >
                    <OverduesAndExpectedDefaultsCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'dueVsExpected' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Due vs Proposed Collections"
                >
                    <DueVsExpectedCollectionsCard
                        dueVsExpectedRows={dueVsExpectedRows}
                        formatAmount={formatAmount}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'nachExpected' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="NACH Expected"
                >
                    <NACHExpectedCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'inflowExpected' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Inflow Expected"
                >
                    <InflowExpectedCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'dsraExpected' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Collection from DSRA"
                >
                    <DSRAExpectedCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'surplusExpected' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Collection from Surplus"
                >
                    <SurplusExpectedCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                        renderMapRows={renderMapRows}
                        hideTitle={true}
                    />
                </ModalUI>
            )}
            {openPopup === 'currentDemandBreakup' && (
                <ModalUI
                    isOpen={true}
                    onRequestClose={() => setOpenPopup(null)}
                    titleLabel="Current Dues Breakup"
                >
                    <CurrentDemandBreakupCard
                        totals={totals}
                        isRetail={isRetail}
                        fmt={fmt}
                        pick={pick}
                    />
                </ModalUI>
            )}
        </CardUI>
    );
};

const EMITrackerSummary2UI = ({ totals, isRetail, auditLogsResponse }) => {
    const fmt = (v) => (v === null || v === undefined || v === '') ? '-' : v
    const pick = (base, retail) => isRetail ? retail : base

    const parseAmountToNumber = (value) => {
        if (value === null || value === undefined) return null
        if (typeof value === 'number' && Number.isFinite(value)) return value
        if (typeof value === 'string') {
            const cleaned = value.replace(/,/g, '').replace(/[^\d.-]/g, '')
            if (!cleaned) return null
            const parsed = parseFloat(cleaned)
            return Number.isFinite(parsed) ? parsed : null
        }
        if (typeof value === 'object' && value !== null && typeof value.toString === 'function') {
            return parseAmountToNumber(value.toString())
        }
        return null
    }

    const formatAmount = (value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return '-'
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) return '-'
        return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const candidateSummaryMaps = [
        'collectionsSummaryMap',
        'collectionSummaryMap',
        'particularsSummaryMap',
        'grandCollectionsSummaryMap',
        'dueSummaryMap',
        'grandDueSummaryMap'
    ]

    const resolveFromMaps = (label) => {
        if (!totals) return null
        for (const mapName of candidateSummaryMaps) {
            const mapObj = totals[mapName]
            if (mapObj && typeof mapObj === 'object') {
                if (Object.prototype.hasOwnProperty.call(mapObj, label)) {
                    const parsed = parseAmountToNumber(mapObj[label])
                    if (parsed !== null) return parsed
                }
                const lowerLabel = label.toLowerCase()
                if (Object.prototype.hasOwnProperty.call(mapObj, lowerLabel)) {
                    const parsed = parseAmountToNumber(mapObj[lowerLabel])
                    if (parsed !== null) return parsed
                }
                const snakeLabel = label.replace(/\s+/g, '_').toLowerCase()
                if (Object.prototype.hasOwnProperty.call(mapObj, snakeLabel)) {
                    const parsed = parseAmountToNumber(mapObj[snakeLabel])
                    if (parsed !== null) return parsed
                }
            }
        }
        return null
    }

    const resolveFromTotals = (keys = []) => {
        if (!totals || !Array.isArray(keys)) return null
        for (const key of keys) {
            if (!key) continue
            const raw = totals[key]
            if (raw !== undefined && raw !== null && raw !== '') {
                const parsed = parseAmountToNumber(raw)
                if (parsed !== null) return parsed
            }
        }
        return null
    }

    const expectedDefaultsMap = pick(
        totals && totals.expectedFurtherDefaultAccountTotalMap,
        totals && totals.expectedFurtherDefaultAccountTotalRMap
    )

    const accountLookupByName = React.useMemo(() => {
        const lookup = {}
        const accountList = Array.isArray(totals && totals.accountEMITrackerList) ? totals.accountEMITrackerList : []
        accountList.forEach((account) => {
            if (!account || !account.accountName) return
            if (isRetail && account.accountType !== 'individual') return
            if (!isRetail && account.accountType !== 'business') return
            lookup[account.accountName] = account
        })
        return lookup
    }, [totals, isRetail])

    const particularFieldMap = {
        // Sum of current and overdue principal
        'Total Principal': ['principalTotalF4', 'overduePrincipalTotalF4'],
        // Sum of current and overdue interest
        'Total Interest': ['interestTotalF4', 'overdueInterestTotalF4'],
        'Total EMI': ['emiTotalF4'],
        'GST Collections': ['gstTotalF4'],
        // Sum of current and overdue penal charges
        'TDS (Int and lease) Collections': ['tdsTotalF4'],
        'Int on Overdue OOPE': ['intOnOverdueOOPETotalCurrentF4', 'intOnOverdueOOPETotalF4'],
        // Sum of current and overdue OOPE
        'OOPE': ['oopeTotalF4', 'overdueOOPETotalF4'],
        // Sum of current and overdue interest on overdue OOPE
        'Penal charges Collections': ['defaultIntTotalF4', 'overdueDefaultIntTotalF4'],
        // Sum of current and overdue interest on overdue Interest
        'Int on Overdue Int': ['intOnOverdueIntTotalCurrentF4', 'intOnOverdueIntTotalF4'],
        // Sum of current and overdue interest on overdue Principal
        'Int on Overdue Principal': ['intOnOverduePrincipalTotalCurrentF4', 'intOnOverduePrincipalTotalF4'],
        // 'Int on Overdue Legal Fee': ['intOnOverdueLegalFeeTotalF4'],
        // 'Overdue Interest': ['overdueInterestTotalF4'],
        // 'Overdue Principal': ['overduePrincipalTotalF4'],
        'Total Amount': ['toCollectTotalF4'],
    }

    const resolveDueFromKeys = (keysToUse) => {
        if (!keysToUse) return null
        const entries = Array.isArray(keysToUse) ? keysToUse : [keysToUse]
        for (const entry of entries) {
            if (Array.isArray(entry)) {
                let sum = 0
                let found = false
                entry.forEach((key) => {
                    const value = resolveFromTotals([key])
                    if (value !== null) {
                        sum += value
                        found = true
                    }
                })
                if (found) return sum
            } else {
                const value = resolveFromTotals([entry])
                if (value !== null) return value
            }
        }
        return null
    }

    const calculateExpectedDefaultsForParticular = React.useCallback((particular, overrideFieldList) => {
        const fieldKeys = Array.isArray(overrideFieldList) && overrideFieldList.length > 0
            ? overrideFieldList
            : particularFieldMap[particular]
        if (!fieldKeys || !expectedDefaultsMap || typeof expectedDefaultsMap !== 'object') return 0

        let total = 0
        Object.keys(expectedDefaultsMap).forEach((accountName) => {
            const account = accountLookupByName[accountName]
            if (!account) return
            fieldKeys.forEach((fieldKey) => {
                // console.log('fieldKey', fieldKey)
                const amount = parseAmountToNumber(account[fieldKey])
                if (amount !== null) {
                    total += amount
                }
            })
        })
        return total
    }, [expectedDefaultsMap, accountLookupByName, particularFieldMap])

    const summaryConfigs = [
        {
            // Sum of current and overdue principal
            label: 'Total Principal',
            baseKeys: [
                ['grandPrincipalTotalF4', 'grandOverduePrincipalTotalF4'],
                'totalPrincipalDueF4',
                'grandPrincipalAmountF4'
            ],
            retailKeys: [
                ['grandPrincipalTotalRF4', 'grandOverduePrincipalTotalRF4'],
                'totalPrincipalDueRF4',
                'grandPrincipalAmountRF4'
            ],
        },
        {
            // Sum of current and overdue interest
            label: 'Total Interest',
            baseKeys: [
                ['grandInterestTotalF4', 'grandOverdueInterestTotalF4'],
                'totalInterestDueF4',
                'grandInterestAmountF4'
            ],
            retailKeys: [
                ['grandInterestTotalRF4', 'grandOverdueInterestTotalRF4'],
                'totalInterestDueRF4',
                'grandInterestAmountRF4'
            ],
        },
        {
            label: 'Total EMI',
            baseKeys: ['grandEmiTotalF4', 'grandEMITotalF4', 'totalEmiDueF4'],
            retailKeys: ['grandEmiTotalRF4', 'grandEMITotalRF4', 'totalEmiDueRF4'],
        },
        {
            label: 'GST Collections',
            baseKeys: ['grandGstTotalF4', 'grandGSTTotalF4', 'totalGstDueF4'],
            retailKeys: ['grandGstTotalRF4', 'grandGSTTotalRF4', 'totalGstDueRF4'],
        },
        {
            label: 'TDS (Int and lease) Collections',
            baseKeys: ['grandTDSTotalF4', 'totalTDSDueF4'],
            retailKeys: ['grandTDSTotalRF4', 'totalTDSDueRF4'],
        },
        {
            // Sum of current and overdue interest on overdue OOPE
            label: 'Int on Overdue OOPE',
            baseKeys: [
                ['grandIntOnOverdueOOPECurrentTotalF4', 'grandIntOnOverdueOOPETotalF4'],
                'totalIntOnOverdueOOPEDueCurrentF4'
            ],
            retailKeys: [
                ['grandIntOnOverdueOOPECurrentTotalRF4', 'grandIntOnOverdueOOPETotalRF4'],
                'totalIntOnOverdueOOPEDueCurrentRF4'
            ],
        },
        {
            // Sum of current and overdue OOPE
            label: 'OOPE',
            baseKeys: [
                ['grandOOPETotalF4', 'grandOverdueOOPETotalF4'],
                'totalOOPEDueF4',
                'oopeTotalF4'
            ],
            retailKeys: [
                ['grandOOPETotalRF4', 'grandOverdueOOPETotalRF4'],
                'totalOOPEDueRF4',
                'oopeTotalRF4'
            ],
        },
        {
            // Sum of current and overdue penal charges
            label: 'Penal charges (incl GST) Collections',
            baseKeys: [
                ['grandDefaultIntTotalF4', 'grandOverdueDefaultIntTotalF4'],
                ['totalPenalChargesDueF4'],
                ['grandPenalChargesTotalF4']
            ],
            retailKeys: [
                ['grandDefaultIntTotalRF4', 'grandOverdueDefaultIntTotalRF4'],
                ['totalPenalChargesDueRF4'],
                ['grandPenalChargesTotalRF4']
            ],
            accountFields: ['defaultIntTotalF4', 'overdueDefaultIntTotalF4'],
        },
        {
            // Sum of current and overdue interest on overdue Interest
            label: 'Int on Overdue Int',
            baseKeys: [
                ['grandIntOnOverdueIntCurrentTotalF4', 'grandIntOnOverdueIntTotalF4'],
                'totalIntOnOverdueIntDueCurrentF4'
            ],
            retailKeys: [
                ['grandIntOnOverdueIntCurrentTotalRF4', 'grandIntOnOverdueIntTotalRF4'],
                'totalIntOnOverdueIntDueCurrentRF4'
            ],
        },
        {
            // Sum of current and overdue interest on overdue Principal
            label: 'Int on Overdue Principal',
            baseKeys: [
                ['grandIntOnOverduePrincipalCurrentTotalF4', 'grandIntOnOverduePrincipalTotalF4'],
                'totalIntOnOverduePrincipalDueCurrentF4'
            ],
            retailKeys: [
                ['grandIntOnOverduePrincipalCurrentTotalRF4', 'grandIntOnOverduePrincipalTotalRF4'],
                'totalIntOnOverduePrincipalDueCurrentF4'
            ],
        },

        {
            label: 'Overdue Interest',
            baseKeys: ['grandOverdueInterestTotalF4', 'totalOverdueInterestDueF4'],
            retailKeys: ['grandOverdueInterestTotalRF4', 'totalOverdueInterestDueRF4'],
        },
        {
            label: 'Overdue Principal',
            baseKeys: ['grandOverduePrincipalTotalF4', 'totalOverduePrincipalDueF4'],
            retailKeys: ['grandOverduePrincipalTotalRF4', 'totalOverduePrincipalDueRF4'],
        },
        {
            label: 'Total Amount',
            baseKeys: ['grandTotalAmountF4', 'grandTotalCollectionsF4', 'totalAmountDueF4'],
            retailKeys: ['grandTotalAmountRF4', 'grandTotalCollectionsRF4', 'totalAmountDueRF4'],
        }
    ]

    const dueVsExpectedRows = summaryConfigs.map(({ label, baseKeys, retailKeys, accountFields }) => {
        const keysToUse = isRetail ? retailKeys : baseKeys
        let dueValue = resolveDueFromKeys(keysToUse)
        if (dueValue === null) {
            dueValue = resolveFromMaps(label)
        }
        if (dueValue === null) return { label, due: null, expected: null, difference: null }

        // Overdue amounts are not expected to be collected in current period, so expected = 0
        const isOverdueAmount = label === 'Overdue Interest' || label === 'Overdue Principal'
        let expected = 0
        let deduction = 0

        if (!isOverdueAmount) {
            deduction = accountFields && accountFields.length > 0
                ? calculateExpectedDefaultsForParticular(label, accountFields)
                : calculateExpectedDefaultsForParticular(label)
            expected = (dueValue - deduction) > 0 ? (dueValue - deduction) : 0
        }

        const difference = dueValue - expected
        return { label, due: dueValue, expected, difference }
    })

    let totalDue = 0
    let totalExpected = 0
    dueVsExpectedRows.forEach((row) => {
        // Exclude Total Amount, Total Principal, and Total Interest from total sum
        // because Total EMI already includes Principal and Interest
        if (row.label === 'Total Amount' || row.label === 'Total Principal' || row.label === 'Total Interest') return
        if (typeof row.due === 'number' && Number.isFinite(row.due)) {
            // TDS should be subtracted, not added (it's a deduction)
            if (row.label === 'TDS (Int and lease) Collections') {
                totalDue -= row.due
            } else {
                totalDue += row.due
            }
        }
        if (typeof row.expected === 'number' && Number.isFinite(row.expected)) {
            // TDS should be subtracted, not added (it's a deduction)
            if (row.label === 'TDS (Int and lease) Collections') {
                totalExpected -= row.expected
            } else {
                totalExpected += row.expected
            }
        }
    })
    const totalDifference = totalDue - totalExpected

    const totalRowIndex = dueVsExpectedRows.findIndex(row => row.label === 'Total Amount')
    if (totalRowIndex >= 0) {
        dueVsExpectedRows[totalRowIndex] = {
            ...dueVsExpectedRows[totalRowIndex],
            due: totalDue,
            expected: totalExpected,
            difference: totalDifference,
            isTotal: true,
        }
    }

    const renderMapRows = (mapObj) => {
        if (!mapObj) return null
        return Object.keys(mapObj).map((k, idx) => (
            <tr key={idx}>
                <td className='left' style={{ padding: '8px' }}>{k}</td>
                <td className='g_text_right' style={{ padding: '8px' }}>{fmt(mapObj[k])}</td>
            </tr>
        ))
    }

    return (
        <div
            className='g_flex_h g_m_top_50'
            style={{
                alignItems: 'flex-start',
                gap: '20px'
            }}
        >
            <div style={{ flex: 3.5 }}>
            <SummaryTableCard
                totals={totals}
                isRetail={isRetail}
                fmt={fmt}
                pick={pick}
                formatAmount={formatAmount}
                parseAmountToNumber={parseAmountToNumber}
                renderMapRows={renderMapRows}
                dueVsExpectedRows={dueVsExpectedRows}
                auditLogsResponse={auditLogsResponse}
            />
            </div>
    
            <div style={{ flex: 1.5 }}>
            <AuditLogsCard
                auditLogsResponse={auditLogsResponse}
            />
            </div>
        </div>
    )
}

const AuditLogsCard = ({ auditLogsResponse }) => {

    const currentStatus = auditLogsResponse && auditLogsResponse.collectionStatus
        ? auditLogsResponse.collectionStatus
        : 'NOT_STARTED'

    const formatDate = (date) => {
        if (!date) return '-'

        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    const rows = [
        {
            label: 'NACH File',
            value: auditLogsResponse && auditLogsResponse.nachfileProcessedDateISO,
            status: auditLogsResponse && !auditLogsResponse.nachfileProcessedDateISO ? 'NOT RECEIVED' : "COMPLETED"
        },
        {
            label: 'Batch Mode Enabled',
            value: auditLogsResponse && auditLogsResponse.batchModeEnablingCompletedDateISO,
            status: auditLogsResponse && auditLogsResponse.batchModeEnablingCompletedDateISO ? "COMPLETED" : auditLogsResponse && auditLogsResponse.collectionCompletedDateISO ? "NOT COMPLETED" : "NOT INITIATED"
        },
        {
            label: 'Collections',
            value: auditLogsResponse && auditLogsResponse.collectionCompletedDateISO,
            status: auditLogsResponse && auditLogsResponse.collectionCompletedDateISO ? "COMPLETED" : auditLogsResponse && auditLogsResponse.emiTrackerSavedDateISO ? "NOT COMPLETED" : "NOT INITIATED"
        },
        {
            label: 'EMI Tracker Saved',
            value: auditLogsResponse && auditLogsResponse.emiTrackerSavedDateISO,
            status: auditLogsResponse && auditLogsResponse.emiTrackerSavedDateISO ? "COMPLETED" : auditLogsResponse && auditLogsResponse.payableApprovalCompletedDateISO ? "NOT COMPLETED" : "NOT INITIATED"
        },
        {
            label: 'Payments Initiated',
            value: auditLogsResponse && auditLogsResponse.payableApprovalCompletedDateISO,
            status: auditLogsResponse && auditLogsResponse.payableApprovalCompletedDateISO ? "COMPLETED" : auditLogsResponse && auditLogsResponse.processPaymentFileCompletedDateISO ? "NOT COMPLETED" : "NOT INITIATED"
        },
        {
            label: 'Payment File',
            value: auditLogsResponse && auditLogsResponse.processPaymentFileCompletedDateISO,
            status: auditLogsResponse && auditLogsResponse.processPaymentFileCompletedDateISO ? "COMPLETED" : auditLogsResponse && auditLogsResponse.normalModeEnabledDateISO ? "NOT COMPLETED" : "NOT INITIATED"
        },
        {
            label: 'Batch Mode Disabled',
            value: auditLogsResponse && auditLogsResponse.normalModeEnabledDateISO,
            status: auditLogsResponse && auditLogsResponse.normalModeEnabledDateISO ? "COMPLETED" : "NOT INITIATED"
        }
    ]

    return (
        <CardUI
            title='Collection Status'
            style={{
                width: '0px',
                flexShrink: 0
            }}
        >
            <div style={{ marginBottom: '15px' }}>
                <div
                    style={{
                        fontSize: '12px',
                        color: '#777'
                    }}
                >
                    Current Step
                </div>

                <div
    style={{
        fontWeight: 'bold',
        fontSize: '16px',
        marginTop: '5px',
        wordBreak: 'break-word',
        color: '#2E7D32'
    }}
>
    {currentStatus}
</div>
            </div>

            <details>
                <summary
                    style={{
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    View Audit Trail
                </summary>

                <table
                    style={{
                        marginTop: '10px',
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '13px'
                    }}
                    className='table_cr table'
                >
                    <tbody>
                        {
                            rows.map(row => (
                                <tr key={row.label}>
                                    <td style={{ width: '55%' }}>
                                        {row.label}
                                    </td>
                                    <td style={{ width: '75%' }}>
                                        {formatDate(row.value)}
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        {row.status}
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </details>
        </CardUI>
    )
}

export default EMITracker2PageUI;